import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Printer, ClipboardList } from 'lucide-react';
import { companyService } from '@/services/company-service';
import { printProjectNotes } from '@/lib/print-notes';
import { revisionCount } from '@/lib/revision-label';
import { printProjectOverview } from '@/lib/print-project-overview';
import { proposalsService } from '@/services/proposals-service';
import { estimatesService } from '@/services/estimates-service';
import { exclusionsService } from '@/services/exclusions-service';
import { assetsService, type EstimateAsset } from '@/services/assets-service';
import { invoicesService } from '@/services/invoices-service';
import { lchService } from '@/services/lch-service';
import { parseLchData, lchCostBySection, computeSectionFinancials, computeEstimateFinancials, parseSubcontractors, subcontractorCostBySection } from '@/lib/section-totals';
import { resolveSelectionTexts } from '@/lib/inclusion-selection';
import { validThroughLabel } from '@/lib/valid-through';

/** Per-image focus points so object-fit:cover crops to the interesting area */
const COVER_PHOTO_FOCUS: Record<string, string> = {
  '/images/covers/cover-1.jpg': 'center center',
  '/images/covers/cover-2.jpg': 'center 70%',
  '/images/covers/cover-3.jpg': 'center center',
  '/images/covers/cover-4.jpg': 'center 40%',
  'https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=1600&h=900&fit=crop&q=80': 'center center',
  'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=900&fit=crop&q=80': 'center 60%',
  'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&h=900&fit=crop&q=80': 'center center',
  'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=1600&h=900&fit=crop&q=80': 'center center',
  'https://images.unsplash.com/photo-1720166671019-744dcef65d05?w=1600&h=900&fit=crop&q=80': 'center center',
  'https://images.unsplash.com/photo-1515162816999-a0c47dc192f7?w=1600&h=900&fit=crop&q=80': 'center center',
};
function getCoverFocus(url: string): string {
  return COVER_PHOTO_FOCUS[url] || 'center center';
}

interface LineItem {
  desc: string;
  qty: string;
  unit: string;
  rate: string;
  total: string;
}

interface CategoryAsset {
  url: string;
  caption?: string;
  description?: string | null;
  assetId?: string;
}

interface WriteupGroup {
  name: string;
  items: string[];
}

interface ProjectCategory {
  name: string;
  groups: WriteupGroup[];
  categoryTotal: string;
  assets: CategoryAsset[];
}

export interface UnitPriceRow {
  item: string;
  description: string;
  unitType: string;
  unitPrice: number;
  quantity: number;
  total: number;
}

export interface Proposal {
  company: string;
  brandColor: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  license: string;
  logoUrl: string;
  coverPhoto: string;
  headerTintEnabled: boolean;
  clientContact: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  clientAddressLine1: string;
  clientAddressLine2: string;
  client: string;
  projectTitle: string;
  projectAddress: string;
  date: string;
  validUntil: string;
  proposalNumber: string;
  /** Revisions so far: 0 = original. Converted from the API's version number. */
  revisionCount?: number;
  estimatorName: string;
  scope: string;
  lineItems: LineItem[];
  categories: ProjectCategory[];
  subtotal: string;
  markup: string;
  markupAmount: string;
  total: string;
  aboutCompany: string;
  aboutSections: { label: string; text: string }[];
  terms: string;
  inclusions: string[];
  exclusions: string[];
  additionalNotes: string;
  projectConditions: string;
  mapAssets: { url: string; caption?: string; description?: string | null; assetId?: string }[];
  addendumAssets: { url: string; caption?: string; description?: string | null; assetId?: string }[];
  pinLetters?: Record<string, string>;
  signatureDataUrl?: string;
  signatureName?: string;
  signedAt?: string;
  coverLetter?: string;
  isUnitPricing?: boolean;
  unitPriceItems?: UnitPriceRow[];
  discountType?: string;
  discountValue?: number;
  showDiscountOnProposal?: boolean;
  discountAmount?: string;
  finalTotal?: string;
  options?: { id: string; name: string; description: string; cost: number }[];
}

/** Format a numeric string with commas (e.g., "12000" → "12,000") */
function fmtWithCommas(raw: string): string {
  if (!raw) return raw;
  const stripped = raw.replace(/,/g, '');
  const num = parseFloat(stripped);
  if (isNaN(num)) return raw;
  const parts = stripped.split('.');
  const intFormatted = parts[0] === '' ? '0' : Math.abs(parseInt(parts[0], 10)).toLocaleString('en-US');
  const sign = stripped.startsWith('-') ? '-' : '';
  return parts.length > 1 ? `${sign}${intFormatted}.${parts[1]}` : `${sign}${intFormatted}`;
}

/* Formats a dollar amount */
const fmtDollars = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Parses a formatted dollar string back to a number */
const parseDollars = (s: string) => parseFloat(s.replace(/[^0-9.]/g, '')) || 0;

/* Checkbox-driven optional add-ons section — shown in HTML proposal view only */
function OptionsAddOnsSection({
  options,
  checkedIds,
  onToggle,
  brandColor,
}: {
  options: NonNullable<Proposal['options']>;
  checkedIds: Set<string>;
  onToggle: (id: string) => void;
  brandColor: string;
}) {
  if (options.length === 0) return null;
  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: brandColor, flexShrink: 0 }} />
        <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Optional Add-Ons</h2>
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
        {options.map((opt, idx) => (
          <label key={opt.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 1rem', fontSize: '13px', borderBottom: idx < options.length - 1 ? '1px solid #e5e7eb' : 'none', backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={checkedIds.has(opt.id)}
              onChange={() => onToggle(opt.id)}
              style={{ marginTop: '3px', accentColor: brandColor, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{opt.name}</div>
              {opt.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{opt.description}</div>}
            </div>
            <div style={{ fontWeight: '600', color: '#1f2937', whiteSpace: 'nowrap' }}>{fmtDollars(opt.cost)}</div>
          </label>
        ))}
      </div>
    </div>
  );
}

/* Hook-like helper: returns toggle fn and adjustedTotal string from proposal + checkedIds */
function useOptionsTotal(proposal: Proposal, checkedIds: Set<string>) {
  const options = proposal.options || [];
  const checkedTotal = options.filter(o => checkedIds.has(o.id)).reduce((s, o) => s + o.cost, 0);
  const base = parseDollars(proposal.finalTotal || proposal.total);
  const displayTotal = checkedTotal > 0 ? fmtDollars(base + checkedTotal) : (proposal.finalTotal || proposal.total);
  return { options, checkedTotal, displayTotal };
}

/* Discount row for proposal summary tables — renders only when discount is active and shown on proposal */
function DiscountRow({ proposal, style }: { proposal: Proposal; style?: React.CSSProperties }) {
  if (!proposal.showDiscountOnProposal || !proposal.discountValue || proposal.discountValue <= 0) return null;
  const label = proposal.discountType === 'percentage'
    ? `Discount (${proposal.discountValue}%)`
    : 'Discount';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '13px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fef3c7', ...style }}>
      <span style={{ color: '#92400e', fontWeight: '500' }}>{label}</span>
      <span style={{ color: '#92400e', fontWeight: '600' }}>-{proposal.discountAmount}</span>
    </div>
  );
}

const sampleProposal: Proposal = {
  company: 'ABC Paving Co',
  brandColor: '#3B82F6',
  phone: '(555) 867-5309',
  email: 'info@abcpaving.com',
  website: 'ABCPaving.com',
  address: '123 Commerce Blvd, Suite 100, Anytown, CA 92101',
  license: 'CA Lic. #1042567',
  logoUrl: '',
  coverPhoto: '/images/covers/cover-1.jpg',
  headerTintEnabled: true,
  client: 'Casa Murrieta Estates HOA',
  clientContact: 'Maria Rodriguez',
  clientEmail: 'maria@casamurrieta.com',
  clientPhone: '(951) 555-0142',
  clientAddress: '45900 Via Murrieta, Murrieta, CA 92562',
  clientAddressLine1: '45900 Via Murrieta',
  clientAddressLine2: 'Murrieta, CA 92562',
  projectTitle: 'Asphalt Repairs & Seal Coat',
  projectAddress: '45900 Via Murrieta, Murrieta, CA 92562',
  date: 'February 26, 2026',
  validUntil: 'March 28, 2026',
  proposalNumber: 'PROP-2026-0001',
  revisionCount: 0,
  estimatorName: 'Jake Thompson',
  scope: 'Mobilize equipment and crew to the project site. Saw cut along designated removal boundaries to establish clean edges. Remove and haul off existing deteriorated asphalt pavement to a depth of 3 inches. Grade and compact the exposed subgrade to achieve proper drainage and a uniform surface. Apply tack coat (SS1H) to all existing edges and surfaces to ensure bonding. Pave with 3 inches of hot mix asphalt (Type B), compacted in a single lift using a steel drum roller to achieve 95% compaction density. Clean up all debris and restore the site to a finished condition.',
  lineItems: [
    { desc: 'Asphalt R&R (2" depth)', qty: '1,850', unit: 'SF', rate: '$4.25', total: '$7,862.50' },
    { desc: 'Crack Seal (hot pour)', qty: '3,200', unit: 'LF', rate: '$1.15', total: '$3,680.00' },
    { desc: 'Seal Coat (2 coats w/ sand)', qty: '28,500', unit: 'SF', rate: '$0.38', total: '$10,830.00' },
    { desc: 'Traffic Control & Barricading', qty: '5', unit: 'Days', rate: '$850.00', total: '$4,250.00' },
    { desc: 'Mobilization', qty: '1', unit: 'LS', rate: '$2,200.00', total: '$2,200.00' }
  ],
  categories: [
    {
      name: 'Asphalt',
      groups: [
        {
          name: 'Remove and Replace',
          items: [
            'Remove 1,850 sq ft at 2 inches thick of asphalt. Cut, load, haul, and dispose of legally.',
            'Grade and compact native soil as needed to place asphalt in approximately 1,850 sq ft.',
            'Place approx. 620 linear ft. SS-1H material along vertical edges of asphalt prior to placement of new material.',
            'Furnish and place 2 inches of PG64-10 asphalt material to pave 4 area(s) totaling approx. 1,850 sq ft.',
          ],
        },
        {
          name: 'Crack Seal',
          items: [
            'Route and clean approximately 3,200 linear ft of cracks greater than 1/4 inch in width.',
            'Apply hot-pour rubberized crack sealant to all routed cracks using direct-fire melter applicator.',
          ],
        },
      ],
      categoryTotal: '$20,030.50',
      assets: [
        { url: 'https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=600&h=400&fit=crop', caption: 'Parking lot — aerial view of project area' },
        { url: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=600&h=400&fit=crop', caption: 'Asphalt surface — cracking and damage' },
      ],
    },
    {
      name: 'Seal Coat',
      groups: [
        {
          name: 'Surface Preparation',
          items: [
            'Power broom and blow all surfaces to remove loose debris, dirt, and vegetation from approximately 28,500 sq ft.',
            'Edge along all curb lines, concrete borders, and painted markings prior to seal coat application.',
          ],
        },
        {
          name: 'Application',
          items: [
            'Apply two (2) coats of coal tar emulsion seal coat with sand aggregate to approximately 28,500 sq ft of asphalt surface.',
            'Provide traffic control barricading and signage for 3-day curing period. No vehicle traffic permitted during cure time.',
          ],
        },
      ],
      categoryTotal: '$13,815.00',
      assets: [
        { url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop', caption: 'Paving crew — asphalt placement in progress' },
        { url: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop', caption: 'Construction team — site inspection' },
      ],
    },
  ],
  subtotal: '$28,822.50',
  markup: '15%',
  markupAmount: '$4,323.38',
  total: '$33,145.88',
  aboutCompany: 'ABC Paving Co has been providing professional paving and asphalt maintenance services since 2005. Our team of experienced professionals is dedicated to delivering quality workmanship on every project.\n\nWe are fully licensed, bonded, and insured.',
  aboutSections: [],
  terms: '1. Payment terms: 50% deposit upon acceptance, balance due upon completion.\n2. This proposal is valid for 30 days from the date above.\n3. Any changes to the scope of work may result in additional charges.\n4. ABC Paving Co carries general liability insurance of $2,000,000.\n5. A 3-year warranty is provided on all asphalt repairs.\n6. Weather delays will extend the project timeline accordingly.',
  inclusions: [
    'All labor, materials, and equipment necessary to complete the scope of work',
    'Traffic control and barricading during construction',
    'Cleanup and removal of all debris upon project completion',
    'Three-year warranty on all asphalt repairs',
  ],
  exclusions: [
    'Concrete curb, gutter, or sidewalk repairs',
    'Underground utility work or adjustments',
    'Permitting fees or engineering costs',
    'Work outside the designated project area',
    'Damage caused by third parties after completion',
  ],
  additionalNotes: '',
  projectConditions: '',
  mapAssets: [
    { url: 'https://images.unsplash.com/photo-1720166671019-744dcef65d05?w=800&h=600&fit=crop', caption: 'Project Area — Aerial View' },
  ],
  addendumAssets: [
    { url: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800&h=600&fit=crop', caption: 'Existing Surface Conditions' },
    { url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop', caption: 'Site Access Point' },
  ],
};

export const TEMPLATE_NAMES: Record<string, string> = {
  classic: 'Professional Classic',
  modern: 'Modern Minimal',
  bold: 'Bold Statement',
  technical: 'Technical Detail',
  compact: 'Streamlined Compact',
};

// Build default writeup groups from projectTypes — mirrors the WriteupPanel builder functions
// Used as a fallback when estimate has no saved scopeData yet
function buildDefaultWriteupGroups(projectTypes: string[]): any[] {
  const builders: Record<string, () => any> = {
    asphalt: () => ({
      id: 'asphalt', title: 'Asphalt', collapsed: false,
      rows: [
        { id: 'a-r1', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        { id: 'a-r2', enabled: false, content: ['Place weed killer prior to placement of base/asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        { id: 'a-r6', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        { id: 'a-r7', enabled: false, content: ['Grade and compact native soil as needed to place base / asphalt in an area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      ],
      sections: [
        { id: 'a-sec1', title: 'Remove & Replace', collapsed: false, rows: [
          { id: 'a-sr3', enabled: false, content: ['Remove ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of asphalt. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-sr4', enabled: false, content: ['Mill out ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of asphalt. Cut, load, haul, and dispose of legally.'] },
        ], note: { id: 'a-snote1', content: '' } },
        { id: 'a-sec3', title: 'Overlay', collapsed: false, rows: [
          { id: 'a-or1', enabled: false, content: ['Place approx ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft. SS-1H material prior to placement of asphalt material.'] },
          { id: 'a-or4', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of PG64-10 asphalt material to construct asphalt overlays totaling approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        ], note: { id: 'a-snote3', content: '' } },
      ],
    }),
    grading: () => ({
      id: 'grading', title: 'Grading', collapsed: false,
      rows: [
        { id: 'g-r1', enabled: false, content: ['Grade and compact native soil as needed to place base / asphalt in an area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        { id: 'g-r2', enabled: false, content: ['Place weed killer prior to placement of base/asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        { id: 'g-r3', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches (+ or -) of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      ],
      sections: [],
    }),
    concrete: () => ({
      id: 'concrete', title: 'Concrete', collapsed: false,
      rows: [
        { id: 'c-r1', enabled: false, content: ['Set forms to construct concrete.'] },
        { id: 'c-r2', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 6" CURB 24" GUTTER.'] },
        { id: 'c-r3', enabled: false, content: ['Furnish and place ', { value: '', size: 'md', placeholder: 'area' }, ' square feet of concrete slabs at a thickness of ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches.'] },
        { id: 'c-r4', enabled: false, content: ['Grade and compact native soil as needed in order to place concrete in area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' square feet.'] },
      ],
      sections: [],
    }),
    'seal-coat': () => ({
      id: 'seal-coat', title: 'Seal Coat', collapsed: false,
      rows: [
        { id: 'sc-r1', enabled: false, content: ['Clean existing asphalt paving as needed with high-pressure blowers prior to placement of seal material.'] },
        { id: 'sc-r2', enabled: false, content: ['Clean approx. ', { value: '', size: 'md', placeholder: 'length' }, ' ln. ft. of cracks averaging more than .25" and less than 1" wide, free of debris and vegetation with high-pressure blowers, treat cracks with ground sterilizer, and seal cracks with an ample amount of HOT rubber crack seal.'] },
        { id: 'sc-r6', enabled: false, content: ['Apply ', { value: '', size: 'sm', placeholder: 'coats' }, ' coat of industrial grade seal coat to a ', { value: '', size: 'md', placeholder: 'surface' }, ' surface with an area of ', { value: '', size: 'md', placeholder: 'area' }, ' square feet.'] },
      ],
      sections: [],
    }),
    striping: () => ({
      id: 'striping', title: 'Striping', collapsed: false,
      rows: [
        { id: 'st-r1', enabled: false, content: ['Re-stripe all existing stall lines, directional arrows, and legends as per existing layout.'] },
        { id: 'st-r3', enabled: false, content: ['Stripe ', { value: '', size: 'sm', placeholder: 'qty' }, ' ADA-compliant handicap stall(s) including signage, blue curb, and access aisle markings.'] },
      ],
      sections: [],
    }),
  };

  const groups: any[] = [];
  for (const pt of projectTypes) {
    const builder = builders[pt];
    if (builder) groups.push(builder());
  }
  return groups;
}

function getComplementaryColors(brandColor: string): { light: string; lighter: string; complement: string } {
  const colorMap: Record<string, { light: string; lighter: string; complement: string }> = {
    '#E0A713': { light: '#fdf6e3', lighter: '#fefcf5', complement: '#1369a7' },
    '#333a81': { light: '#e8ecf7', lighter: '#f5f7fc', complement: '#8c7333' },
    '#0074F4': { light: '#e3f0ff', lighter: '#f0f7ff', complement: '#f47400' },
  };
  return colorMap[brandColor] || { light: '#f5f5f0', lighter: '#fafaf8', complement: '#666' };
}

function ProposalLogo({ url, size = 40, style }: { url: string; size?: number; style?: React.CSSProperties }): React.ReactElement | null {
  if (!url) return null;
  return (
    <div style={{ width: size, height: size, borderRadius: 6, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', ...style }}>
      <img src={url} alt="Company logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </div>
  );
}

// ============ CLASSIC TEMPLATE ============
function ClassicTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  return (
    <div className="pp-page" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Hero section — full brand color with cover image overlay */}
      <div style={{ flex: 1, backgroundColor: bc, position: 'relative', display: 'flex', flexDirection: 'column', padding: '2.5rem 3rem' }}>
        {/* Background image with brand color overlay */}
        {proposal.coverPhoto && (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${proposal.coverPhoto})`, backgroundSize: 'cover', backgroundPosition: getCoverFocus(proposal.coverPhoto) }} />
            {proposal.headerTintEnabled && <div style={{ position: 'absolute', inset: 0, backgroundColor: bc, opacity: 0.25 }} />}
          </>
        )}
        {/* Decorative geometric shapes */}
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        {/* Logo — large */}
        <div style={{ position: 'relative', zIndex: 1, marginBottom: '2.5rem' }}>
          <ProposalLogo url={proposal.logoUrl} size={125} style={{ borderRadius: 12, border: '2px solid rgba(255,255,255,0.3)' }} />
        </div>

        {/* Title area */}
        <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h1 style={{ fontSize: '42px', fontWeight: '800', color: 'white', lineHeight: '1.12', letterSpacing: '-0.02em', marginBottom: '1rem' }}>{proposal.projectTitle}</h1>
          <div style={{ height: '4px', width: '60px', backgroundColor: 'rgba(255,255,255,0.4)', borderRadius: '2px', marginBottom: '1rem' }} />
          {proposal.projectAddress && <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '0.15rem' }}>Project Location: {proposal.projectAddress}</p>}
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>{proposal.date} &nbsp;&middot;&nbsp; Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>
        </div>
      </div>

      {/* Dark footer — Prepared For / Submitted By */}
      <div style={{ backgroundColor: '#1e293b', padding: '3rem 3rem', color: 'white', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        <div>
          <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.45)', marginBottom: '0.5rem' }}>Prepared For</p>
          <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.2rem' }}>{proposal.clientContact}</p>
          <p style={{ fontSize: '12px', opacity: 0.8 }}>{proposal.client}</p>
          {proposal.clientAddressLine1 && <p style={{ fontSize: '11px', opacity: 0.55, marginTop: '0.25rem' }}>{proposal.clientAddressLine1}</p>}
          {proposal.clientAddressLine2 && <p style={{ fontSize: '11px', opacity: 0.55 }}>{proposal.clientAddressLine2}</p>}
          <p style={{ fontSize: '11px', opacity: 0.55 }}>{proposal.clientPhone}</p>
          {proposal.clientEmail && <p style={{ fontSize: '11px', opacity: 0.55 }}>{proposal.clientEmail}</p>}
        </div>
        <div>
          <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.45)', marginBottom: '0.5rem' }}>Submitted By</p>
          <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.2rem' }}>{proposal.estimatorName}</p>
          <p style={{ fontSize: '12px', opacity: 0.8 }}>{proposal.company}</p>
          <p style={{ fontSize: '11px', opacity: 0.55, marginTop: '0.25rem', lineHeight: '1.5' }}>{proposal.address}</p>
          <p style={{ fontSize: '11px', opacity: 0.55 }}>{proposal.phone}</p>
        </div>
      </div>
    </div>
  );
}

function ClassicTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, checkedTotal, displayTotal } = useOptionsTotal(proposal, checkedOptions);
  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
      <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: bc }} />
      <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>{children}</h2>
    </div>
  );

  return (
    <div className="pp-page pp-page-inner">
      {/* Header bar */}
      <div style={{ backgroundColor: bc, height: '6px' }} />
      <div style={{ padding: '1.25rem 2rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ProposalLogo url={proposal.logoUrl} size={32} />
          <div>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{proposal.company}</p>
            <p style={{ fontSize: '10px', color: '#9ca3af', margin: 0 }}>{proposal.address}</p>
          </div>
        </div>
        <p style={{ fontSize: '10px', color: '#9ca3af' }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>
      </div>

      {/* Content */}
      <div style={{ padding: '2rem' }}>
        {/* Scope of Work */}
        {proposal.scope && proposal.scope.trim() && (
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>Scope of Work</SectionTitle>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7' }}>{proposal.scope}</p>
        </div>
        )}

        {/* Project Details — categories with writeup descriptions */}
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>Project Details</SectionTitle>
          {proposal.categories.map((cat, cIdx) => (
            <div key={cIdx} style={{ marginBottom: '2rem' }}>
              {/* Category banner */}
              <div style={{ backgroundColor: bc, padding: '0.625rem 1rem', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>{cat.name}</h3>
              </div>
              {/* Writeup groups */}
              {cat.groups.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '1rem' }}>
                  {group.name && <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>{group.name}</h4>}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    {group.items.map((item, iIdx) => (
                      <div key={iIdx} style={{ padding: '0.75rem 1rem', fontSize: '12px', color: '#374151', lineHeight: '1.6', borderBottom: iIdx < group.items.length - 1 ? '1px solid #e5e7eb' : 'none', backgroundColor: iIdx % 2 === 0 ? '#fafafa' : 'white' }}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {/* Category total */}
              <div style={{ padding: '0.625rem 1rem', borderTop: '2px solid #e5e7eb', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: bc }}>{cat.name} Total: </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>{cat.categoryTotal}</span>
                <p style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', marginTop: '0.25rem' }}>Price valid until {proposal.validUntil}</p>
              </div>
              {/* Category assets */}
              {cat.assets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' }}>
                  {cat.assets.map((asset, aIdx) => (
                    <div key={aIdx}>
                      <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '4px', display: 'block' }} />
                      {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.4' }}>{asset.description}</p>}
                      {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
                        <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          See Map — {proposal.pinLetters[asset.assetId]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Optional Add-Ons */}
          <OptionsAddOnsSection options={options} checkedIds={checkedOptions} onToggle={toggleOption} brandColor={bc} />

          {/* Project Summary */}
          <SectionTitle>Project Summary</SectionTitle>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            {proposal.categories.map((cat, cIdx) => (
              <div key={cIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '13px', borderBottom: '1px solid #e5e7eb', backgroundColor: cIdx % 2 === 0 ? '#fafafa' : 'white' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>{cat.name}</span>
                <span style={{ color: '#1f2937', fontWeight: '600' }}>{cat.categoryTotal}</span>
              </div>
            ))}
            <DiscountRow proposal={proposal} />
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '14px', fontWeight: '700', backgroundColor: bc, color: 'white' }}>
              <span>Final Base Bid Total</span>
              <span>{displayTotal}</span>
            </div>
            {checkedTotal > 0 && (
              <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', padding: '0.25rem 1rem' }}>Includes selected optional add-ons</div>
            )}
          </div>
        </div>

        {/* Inclusions & Exclusions */}
        {(proposal.inclusions.length > 0 || proposal.exclusions.length > 0) && (
        <div style={{ marginBottom: '2rem' }}>
          {proposal.inclusions.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <SectionTitle>This Estimate Includes</SectionTitle>
              <ul style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.inclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
          {proposal.exclusions.length > 0 && (
            <div>
              <SectionTitle>This Estimate Does Not Include</SectionTitle>
              <ul style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.exclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Project Conditions */}
        {proposal.projectConditions && (
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>Project Conditions</SectionTitle>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
        )}

        {/* Additional Notes */}
        {proposal.additionalNotes && (
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>Additional Notes</SectionTitle>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
        )}

        {/* About Sections */}
        {proposal.aboutSections.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>A Little About Us</SectionTitle>
          {proposal.aboutSections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < proposal.aboutSections.length - 1 ? '2rem' : 0 }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>{section.label}</p>
              <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{section.text}</p>
            </div>
          ))}
        </div>
        )}

        {/* Terms */}
        {proposal.terms && proposal.terms.trim() && (
        <div style={{ marginBottom: '2rem' }}>
          <SectionTitle>Terms & Conditions</SectionTitle>
          <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.6' }}>
            {proposal.terms.split('\n').map((term, idx) => (
              <p key={idx} style={{ marginBottom: '0.35rem' }}>{term}</p>
            ))}
          </div>
        </div>
        )}

        {/* Signature */}
        <div style={{ marginBottom: '1rem' }}>
          <SectionTitle>Acceptance & Signature</SectionTitle>
          {proposal.signatureDataUrl ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem' }}>
                <div>
                  <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '3rem', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.25rem' }} />
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>{proposal.signatureName || 'Client Signature'}</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Signed: {proposal.signedAt ? new Date(proposal.signedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '3rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2rem', lineHeight: '1.5' }}>By signing below, you accept the terms and conditions of this proposal and authorize {proposal.company} to proceed with the described work.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem' }}>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Client Signature</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer bar */}
      <div style={{ marginTop: 'auto', backgroundColor: bc, padding: '0.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ fontSize: '10px', color: 'white', fontWeight: '600' }}>Thank you for your business!</p>
        <p style={{ fontSize: '10px', color: 'white', opacity: 0.7 }}>{proposal.company}</p>
      </div>
    </div>
  );
}

// ============ MODERN TEMPLATE ============
function ModernTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  return (
    <div className="pp-page" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Clean header area on warm light background */}
      <div style={{ padding: '2.5rem 2.5rem 2rem', backgroundColor: '#fafaf8' }}>
        <ProposalLogo url={proposal.logoUrl} size={113} style={{ borderRadius: 12, marginBottom: '1.5rem', border: '1.5px solid rgba(0,0,0,0.1)' }} />
        <h1 style={{ fontSize: '36px', fontWeight: '300', color: '#1f2937', letterSpacing: '-0.02em', lineHeight: '1.15', marginBottom: '0.5rem' }}>{proposal.projectTitle}</h1>
        {proposal.projectAddress && <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '0.15rem' }}>Project Location: {proposal.projectAddress}</p>}
        <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '0.75rem' }}>{proposal.date} &nbsp;&middot;&nbsp; Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>
        <div style={{ height: '3px', width: '50px', backgroundColor: bc }} />
      </div>

      {/* Thin brand divider */}
      <div style={{ height: '2px', backgroundColor: bc }} />

      {/* Cover photo band with brand color overlay */}
      <div style={{ height: '260px', position: 'relative', overflow: 'hidden' }}>
        {proposal.coverPhoto ? (
          <>
            <img src={proposal.coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: getCoverFocus(proposal.coverPhoto), display: 'block' }} />
            {proposal.headerTintEnabled && <div style={{ position: 'absolute', inset: 0, backgroundColor: bc, opacity: 0.25 }} />}
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${bc}20 0%, ${bc}08 100%)` }} />
        )}
      </div>

      {/* Contact fields below photo */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '1.5rem 2.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div>
            <p style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '0.5rem' }}>Prepared For</p>
            <p style={{ fontSize: '15px', fontWeight: '500', color: '#1f2937', marginBottom: '0.25rem' }}>{proposal.clientContact}</p>
            <p style={{ fontSize: '13px', color: '#374151' }}>{proposal.client}</p>
            {proposal.clientAddressLine1 && <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.25rem' }}>{proposal.clientAddressLine1}</p>}
            {proposal.clientAddressLine2 && <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.clientAddressLine2}</p>}
            <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.clientPhone}</p>
            {proposal.clientEmail && <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.clientEmail}</p>}
          </div>
          <div>
            <p style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '0.5rem' }}>Submitted By</p>
            <p style={{ fontSize: '15px', fontWeight: '500', color: '#1f2937', marginBottom: '0.25rem' }}>{proposal.estimatorName}</p>
            <p style={{ fontSize: '13px', color: '#374151' }}>{proposal.company}</p>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.25rem', lineHeight: '1.5' }}>{proposal.address}</p>
          </div>
        </div>
      </div>

      {/* Bottom accent bar */}
      <div style={{ height: '6px', backgroundColor: bc }} />
    </div>
  );
}

function ModernTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const colors = getComplementaryColors(bc);
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, checkedTotal, displayTotal } = useOptionsTotal(proposal, checkedOptions);
  return (
    <div className="pp-page pp-page-inner">
      <div style={{ padding: '2rem' }}>
        {/* Scope */}
        {proposal.scope && proposal.scope.trim() && (
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scope of Work</p>
          <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.7', marginTop: '0.75rem' }}>{proposal.scope}</p>
        </div>
        )}

        {/* Project Details — categories with writeup descriptions */}
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Project Details</p>
          {proposal.categories.map((cat, cIdx) => (
            <div key={cIdx} style={{ marginBottom: '2rem' }}>
              {/* Category name with accent line */}
              <div style={{ borderLeft: `3px solid ${bc}`, paddingLeft: '0.75rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', textTransform: 'uppercase' }}>{cat.name}</p>
              </div>
              {/* Writeup groups */}
              {cat.groups.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '1rem' }}>
                  {group.name && <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>{group.name}</p>}
                  {group.items.map((item, iIdx) => (
                    <div key={iIdx} style={{ padding: '0.75rem', backgroundColor: colors.lighter, borderRadius: '2px', marginBottom: '0.5rem', fontSize: '12px', color: '#374151', lineHeight: '1.6' }}>
                      {item}
                    </div>
                  ))}
                </div>
              ))}
              {/* Category total */}
              <div style={{ paddingTop: '0.5rem' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: bc }}>{cat.name} Total: </span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>{cat.categoryTotal}</span>
                <p style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', marginTop: '0.25rem' }}>Price valid until {proposal.validUntil}</p>
              </div>
              {/* Category assets */}
              {cat.assets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' }}>
                  {cat.assets.map((asset, aIdx) => (
                    <div key={aIdx}>
                      <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '4px', display: 'block' }} />
                      {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.4' }}>{asset.description}</p>}
                      {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
                        <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          See Map — {proposal.pinLetters[asset.assetId]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Optional Add-Ons */}
        <OptionsAddOnsSection options={options} checkedIds={checkedOptions} onToggle={toggleOption} brandColor={bc} />

        {/* Project Summary */}
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Project Summary</p>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            {proposal.categories.map((cat, cIdx) => (
              <div key={cIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '13px', borderBottom: '1px solid #e5e7eb', backgroundColor: cIdx % 2 === 0 ? colors.lighter : 'white' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>{cat.name}</span>
                <span style={{ color: '#1f2937', fontWeight: '600' }}>{cat.categoryTotal}</span>
              </div>
            ))}
            <DiscountRow proposal={proposal} />
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1rem', fontSize: '14px', fontWeight: '700', backgroundColor: colors.light, borderTop: `2px solid ${bc}` }}>
              <span style={{ color: bc }}>Final Base Bid Total</span>
              <span style={{ color: '#1f2937' }}>{displayTotal}</span>
            </div>
            {checkedTotal > 0 && (
              <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', padding: '0.25rem 1rem' }}>Includes selected optional add-ons</div>
            )}
          </div>
        </div>

        {/* Inclusions & Exclusions */}
        {(proposal.inclusions.length > 0 || proposal.exclusions.length > 0) && (
        <div style={{ marginBottom: '2rem' }}>
          {proposal.inclusions.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>This Estimate Includes</p>
              <ul style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.inclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
          {proposal.exclusions.length > 0 && (
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>This Estimate Does Not Include</p>
              <ul style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.exclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Project Conditions */}
        {proposal.projectConditions && (
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Project Conditions</p>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
        )}

        {/* Additional Notes */}
        {proposal.additionalNotes && (
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Additional Notes</p>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
        )}

        {/* About Sections */}
        {proposal.aboutSections.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>A Little About Us</p>
          {proposal.aboutSections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < proposal.aboutSections.length - 1 ? '2rem' : 0 }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>{section.label}</p>
              <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{section.text}</p>
            </div>
          ))}
        </div>
        )}

        {/* Terms */}
        {proposal.terms && proposal.terms.trim() && (
        <div style={{ marginBottom: '2rem', fontSize: '11px' }}>
          <p style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>Terms & Conditions</p>
          <div style={{ color: '#374151', lineHeight: '1.6' }}>
            {proposal.terms.split('\n').map((term, idx) => (
              <p key={idx} style={{ marginBottom: '0.25rem' }}>{term}</p>
            ))}
          </div>
        </div>
        )}

        {/* Signature */}
        <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: bc, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Acceptance & Signature</p>
          {proposal.signatureDataUrl ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div>
                <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '2.5rem', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.25rem' }} />
                <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>{proposal.signatureName || 'Client Signature'}</p>
                <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Signed: {proposal.signedAt ? new Date(proposal.signedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
              </div>
              <div>
                <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>By signing below, you accept the terms and conditions of this proposal and authorize {proposal.company} to proceed with the described work.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Client Signature</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Thank You */}
        <div style={{ textAlign: 'center', paddingTop: '1rem', color: bc, fontWeight: '500' }}>
          <p>Thank you for your consideration</p>
        </div>
      </div>
    </div>
  );
}

// ============ BOLD TEMPLATE ============
function BoldTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  return (
    <div className="pp-page" style={{ display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
      {/* Left panel — brand color with logo + title */}
      <div style={{ flex: '0 0 40%', backgroundColor: bc, padding: '2.5rem 2rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <ProposalLogo url={proposal.logoUrl} size={113} style={{ borderRadius: 12, marginBottom: '2rem', border: '2px solid rgba(255,255,255,0.3)' }} />
          <h1 style={{ fontSize: '32px', fontWeight: '900', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>{proposal.projectTitle}</h1>
          <div style={{ height: '3px', width: '40px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '2px', marginBottom: '0.75rem' }} />
          {proposal.projectAddress && <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '0.15rem' }}>Project Location: {proposal.projectAddress}</p>}
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{proposal.date}</p>
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>
        </div>
        <div>
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.5' }}>{proposal.company}</p>
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.5' }}>{proposal.phone}</p>
        </div>
      </div>

      {/* Right panel — photo + contact fields */}
      <div style={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column' }}>
        {/* Cover photo with brand color overlay */}
        <div style={{ height: '55%', position: 'relative', overflow: 'hidden' }}>
          {proposal.coverPhoto ? (
            <>
              <img src={proposal.coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: getCoverFocus(proposal.coverPhoto), display: 'block' }} />
              {proposal.headerTintEnabled && <div style={{ position: 'absolute', inset: 0, backgroundColor: bc, opacity: 0.25 }} />}
            </>
          ) : (
            <div style={{ width: '100%', height: '100%', backgroundColor: '#1e293b' }} />
          )}
        </div>

        {/* Contact fields */}
        <div style={{ flex: 1, padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div>
            <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#9ca3af', marginBottom: '0.5rem' }}>Prepared For</p>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '0.15rem' }}>{proposal.clientContact}</p>
            <p style={{ fontSize: '12px', color: '#4b5563' }}>{proposal.client}</p>
            {proposal.clientAddressLine1 && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.2rem' }}>{proposal.clientAddressLine1}</p>}
            {proposal.clientAddressLine2 && <p style={{ fontSize: '11px', color: '#9ca3af' }}>{proposal.clientAddressLine2}</p>}
            <p style={{ fontSize: '11px', color: '#9ca3af' }}>{proposal.clientPhone}</p>
            {proposal.clientEmail && <p style={{ fontSize: '11px', color: '#9ca3af' }}>{proposal.clientEmail}</p>}
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#9ca3af', marginBottom: '0.5rem' }}>Submitted By</p>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '0.15rem' }}>{proposal.estimatorName}</p>
            <p style={{ fontSize: '12px', color: '#4b5563' }}>{proposal.company}</p>
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.2rem', lineHeight: '1.5' }}>{proposal.address}</p>
          </div>
          <div style={{ marginTop: 'auto', paddingTop: '0.75rem' }}>
            <p style={{ fontSize: '10px', color: '#9ca3af' }}>Valid until {proposal.validUntil}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BoldTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const scopeLines = proposal.scope.split('\n\n');
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, checkedTotal, displayTotal } = useOptionsTotal(proposal, checkedOptions);
  return (
    <div className="pp-page pp-page-inner" style={{ borderLeft: `5px solid ${bc}` }}>
      <div style={{ padding: '2rem' }}>
        {/* Scope */}
        {proposal.scope && proposal.scope.trim() && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 'bold', color: bc, marginBottom: '1rem', textTransform: 'uppercase' }}>Scope of Work</h2>
          {scopeLines.map((line, idx) => (
            <div key={idx} style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <span style={{ color: bc, fontWeight: 'bold', flexShrink: 0 }}>•</span>
                <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.6' }}>{line.trim()}</p>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Project Details — categories with writeup descriptions */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 'bold', color: bc, marginBottom: '1rem', textTransform: 'uppercase' }}>Project Details</h2>
          {proposal.categories.map((cat, cIdx) => (
            <div key={cIdx} style={{ marginBottom: '2rem' }}>
              {/* Category banner */}
              <div style={{ backgroundColor: bc, padding: '0.5rem 1rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '13px', fontWeight: '700', color: 'white', textTransform: 'uppercase', margin: 0 }}>{cat.name}</p>
              </div>
              {/* Writeup groups */}
              {cat.groups.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '1rem' }}>
                  {group.name && <p style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>{group.name}</p>}
                  {group.items.map((item, iIdx) => (
                    <div key={iIdx} style={{ paddingBottom: '0.75rem', marginBottom: '0.75rem', borderBottom: '1px solid #e5e7eb', fontSize: '12px', color: '#374151', lineHeight: '1.6' }}>
                      {item}
                    </div>
                  ))}
                </div>
              ))}
              {/* Category total */}
              <div style={{ paddingTop: '0.25rem' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: bc }}>{cat.name} Total: </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>{cat.categoryTotal}</span>
                <p style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', marginTop: '0.25rem' }}>Price valid until {proposal.validUntil}</p>
              </div>
              {/* Category assets */}
              {cat.assets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' }}>
                  {cat.assets.map((asset, aIdx) => (
                    <div key={aIdx}>
                      <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '4px', display: 'block' }} />
                      {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.4' }}>{asset.description}</p>}
                      {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
                        <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          See Map — {proposal.pinLetters[asset.assetId]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Optional Add-Ons */}
          <OptionsAddOnsSection options={options} checkedIds={checkedOptions} onToggle={toggleOption} brandColor={bc} />

          {/* Project Summary */}
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>Project Summary</p>
            {proposal.categories.map((cat, cIdx) => (
              <div key={cIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0', fontSize: '13px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>{cat.name}</span>
                <span style={{ color: '#1f2937', fontWeight: '600' }}>{cat.categoryTotal}</span>
              </div>
            ))}
            <DiscountRow proposal={proposal} />
            <div style={{ padding: '0.75rem 1rem', backgroundColor: bc, color: 'white', borderRadius: '2px', marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: '700' }}>
              <span>Final Base Bid Total</span>
              <span>{displayTotal}</span>
            </div>
            {checkedTotal > 0 && (
              <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', padding: '0.25rem 0' }}>Includes selected optional add-ons</div>
            )}
          </div>
        </div>

        {/* Inclusions & Exclusions */}
        {(proposal.inclusions.length > 0 || proposal.exclusions.length > 0) && (
        <div style={{ marginBottom: '2rem' }}>
          {proposal.inclusions.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>This Estimate Includes</p>
              <ul style={{ fontSize: '11px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.inclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
          {proposal.exclusions.length > 0 && (
            <div>
              <p style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>This Estimate Does Not Include</p>
              <ul style={{ fontSize: '11px', color: '#374151', lineHeight: '1.7', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.exclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Project Conditions */}
        {proposal.projectConditions && (
        <div style={{ marginBottom: '2rem', fontSize: '11px' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.5rem' }}>Project Conditions</p>
          <p style={{ color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
        )}

        {/* Additional Notes */}
        {proposal.additionalNotes && (
        <div style={{ marginBottom: '2rem', fontSize: '11px' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.5rem' }}>Additional Notes</p>
          <p style={{ color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
        )}

        {/* About Sections */}
        {proposal.aboutSections.length > 0 && (
        <div style={{ marginBottom: '2rem', fontSize: '11px' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.5rem' }}>A Little About Us</p>
          {proposal.aboutSections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < proposal.aboutSections.length - 1 ? '1.5rem' : 0 }}>
              <p style={{ fontWeight: '600', color: '#374151', marginBottom: '0.15rem' }}>{section.label}</p>
              <p style={{ color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{section.text}</p>
            </div>
          ))}
        </div>
        )}

        {/* Terms */}
        {proposal.terms && proposal.terms.trim() && (
        <div style={{ marginBottom: '2rem', fontSize: '10px' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.5rem' }}>Terms & Conditions</p>
          <div style={{ color: '#374151', lineHeight: '1.5' }}>
            {proposal.terms.split('\n').map((term, idx) => (
              <p key={idx} style={{ marginBottom: '0.25rem' }}>{term}</p>
            ))}
          </div>
        </div>
        )}

        {/* Signature */}
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.75rem', fontSize: '10px' }}>Acceptance & Signature</p>
          {proposal.signatureDataUrl ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', fontSize: '10px' }}>
              <div>
                <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '2.5rem', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.25rem' }} />
                <div style={{ borderBottom: '3px solid #d1d5db', marginBottom: '0.5rem' }} />
                <p style={{ fontWeight: '600', color: '#374151' }}>{proposal.signatureName || 'Client Signature'}</p>
                <p style={{ color: '#9ca3af', marginTop: '0.25rem' }}>Signed: {proposal.signedAt ? new Date(proposal.signedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
              </div>
              <div>
                <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                <p style={{ fontWeight: '600', color: '#374151' }}>Company Representative</p>
                <p style={{ color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '10px', color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>By signing below, you accept the terms and conditions of this proposal and authorize {proposal.company} to proceed with the described work.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', fontSize: '10px' }}>
                <div>
                  <div style={{ borderBottom: '3px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontWeight: '600', color: '#374151' }}>Client Signature</p>
                  <p style={{ color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ paddingTop: '1rem', borderTop: `3px solid ${bc}`, textAlign: 'center', color: bc, fontWeight: '600' }}>
          <p style={{ fontSize: '12px' }}>We look forward to serving you!</p>
        </div>
      </div>
    </div>
  );
}

// ============ TECHNICAL TEMPLATE ============
function TechnicalTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const dataRows = [
    { label: 'Client', value: proposal.clientContact },
    { label: 'Company', value: proposal.client },
    { label: 'Email', value: proposal.clientEmail },
    { label: 'Project Location', value: proposal.clientAddress },
    { label: 'Estimator', value: proposal.estimatorName },
    { label: 'Date Issued', value: proposal.date },
    { label: 'Valid Until', value: proposal.validUntil },
  ].filter(r => r.value);
  return (
    <div className="pp-page" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Gray header bar with brand bottom border */}
      <div style={{ backgroundColor: '#f3f4f6', borderBottom: `3px solid ${bc}`, padding: '1.25rem 2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 'bold', color: bc, marginBottom: '0.35rem' }}>REF #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · REV ${proposal.revisionCount}` : ''}</p>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', lineHeight: '1.2' }}>{proposal.projectTitle}</h1>
          {proposal.projectAddress && <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '0.35rem', marginBottom: '0.15rem' }}>Project Location: {proposal.projectAddress}</p>}
        </div>
        <ProposalLogo url={proposal.logoUrl} size={100} style={{ borderRadius: 10, flexShrink: 0, marginLeft: '1rem', border: '1.5px solid rgba(0,0,0,0.1)' }} />
      </div>

      {/* Data table rows */}
      <div style={{ padding: '0 2.5rem' }}>
        {dataRows.map((row, idx) => (
          <div key={idx} style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0.6rem 0.75rem', backgroundColor: idx % 2 === 0 ? '#f9fafb' : '#fff' }}>
            <span style={{ flex: '0 0 35%', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{row.label}</span>
            <span style={{ flex: 1, fontSize: '12px', color: '#1f2937' }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* Cover photo with brand color overlay */}
      <div style={{ height: '280px', margin: '1.25rem 2.5rem 0', position: 'relative', overflow: 'hidden', borderRadius: '4px' }}>
        {proposal.coverPhoto ? (
          <>
            <img src={proposal.coverPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: getCoverFocus(proposal.coverPhoto), display: 'block' }} />
            {proposal.headerTintEnabled && <div style={{ position: 'absolute', inset: 0, backgroundColor: bc, opacity: 0.25 }} />}
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)` }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.3, backgroundImage: `repeating-linear-gradient(0deg, ${bc}20 0px, ${bc}20 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, ${bc}20 0px, ${bc}20 1px, transparent 1px, transparent 40px)` }} />
          </div>
        )}
      </div>

      {/* Bottom section — brand accent + contact */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '1rem 2.5rem 1.5rem' }}>
        <div style={{ borderTop: `2px solid ${bc}`, paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6b7280' }}>
          <span>{proposal.company} &nbsp;&middot;&nbsp; {proposal.phone}</span>
          <span>{proposal.email}</span>
        </div>
      </div>
    </div>
  );
}

function TechnicalTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, checkedTotal, displayTotal } = useOptionsTotal(proposal, checkedOptions);
  return (
    <div className="pp-page pp-page-inner">
      <div style={{ padding: '2rem' }}>
        {/* Scope Section */}
        {proposal.scope && proposal.scope.trim() && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '1rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Scope Document</h2>
          <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7', marginLeft: '1.25rem' }}>
            {proposal.scope.split('\n\n').map((para, idx) => (
              <div key={idx} style={{ marginBottom: '0.75rem' }}>
                <span style={{ color: bc, fontWeight: 'bold', marginRight: '0.5rem' }}>{idx + 1}.</span>
                {para.trim()}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Project Details — categories with writeup descriptions */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '1rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Project Details</h2>
          {proposal.categories.map((cat, cIdx) => (
            <div key={cIdx} style={{ marginBottom: '1.5rem' }}>
              <div style={{ backgroundColor: '#f3f4f6', padding: '0.5rem 0.75rem', borderBottom: `2px solid ${bc}`, marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '11px', fontWeight: 'bold', color: bc, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>{cat.name}</h3>
              </div>
              {/* Writeup groups */}
              {cat.groups.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '0.75rem' }}>
                  {group.name && <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.35rem' }}>{group.name}</p>}
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <tbody>
                      {group.items.map((item, iIdx) => (
                        <tr key={iIdx} style={{ backgroundColor: iIdx % 2 === 0 ? '#f9fafb' : 'white', borderBottom: '1px solid #d1d5db' }}>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#374151', lineHeight: '1.6' }}>{item}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {/* Category total */}
              <div style={{ paddingTop: '0.5rem' }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: bc }}>{cat.name} Total: </span>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1f2937' }}>{cat.categoryTotal}</span>
                <p style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic', marginTop: '0.15rem' }}>Price valid until {proposal.validUntil}</p>
              </div>
              {/* Category assets */}
              {cat.assets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                  {cat.assets.map((asset, aIdx) => (
                    <div key={aIdx}>
                      <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '4px', display: 'block' }} />
                      {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.4' }}>{asset.description}</p>}
                      {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
                        <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          See Map — {proposal.pinLetters[asset.assetId]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Optional Add-Ons */}
          <OptionsAddOnsSection options={options} checkedIds={checkedOptions} onToggle={toggleOption} brandColor={bc} />

          {/* Project Summary */}
          <div style={{ marginTop: '1rem' }}>
            <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Project Summary</h2>
            <div style={{ border: '1px solid #d1d5db', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '0.4rem 0.75rem', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', backgroundColor: '#f3f4f6', borderBottom: '1px solid #d1d5db' }}>
                <span style={{ flex: 1 }}>Category</span>
                <span style={{ width: '120px', textAlign: 'right' }}>Amount</span>
              </div>
              {proposal.categories.map((cat, cIdx) => (
                <div key={cIdx} style={{ display: 'flex', padding: '0.5rem 0.75rem', fontSize: '12px', borderBottom: '1px solid #e5e7eb', backgroundColor: cIdx % 2 === 0 ? '#f9fafb' : 'white' }}>
                  <span style={{ flex: 1, color: '#374151' }}>{cat.name}</span>
                  <span style={{ width: '120px', textAlign: 'right', fontWeight: '600', color: '#1f2937' }}>{cat.categoryTotal}</span>
                </div>
              ))}
              <DiscountRow proposal={proposal} style={{ padding: '0.5rem 0.75rem', fontSize: '12px' }} />
              <div style={{ display: 'flex', padding: '0.625rem 0.75rem', fontSize: '13px', fontWeight: '700', backgroundColor: '#f3f4f6', borderTop: `2px solid ${bc}` }}>
                <span style={{ flex: 1, color: bc }}>Final Base Bid Total</span>
                <span style={{ width: '120px', textAlign: 'right', color: '#1f2937' }}>{displayTotal}</span>
              </div>
              {checkedTotal > 0 && (
                <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', padding: '0.25rem 0.75rem' }}>Includes selected optional add-ons</div>
              )}
            </div>
          </div>
        </div>

        {/* Inclusions & Exclusions */}
        {(proposal.inclusions.length > 0 || proposal.exclusions.length > 0) && (
        <div style={{ marginBottom: '1.5rem' }}>
          {proposal.inclusions.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>This Estimate Includes</h2>
              <ul style={{ fontSize: '11px', color: '#374151', lineHeight: '1.6', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.inclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
          {proposal.exclusions.length > 0 && (
            <div>
              <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>This Estimate Does Not Include</h2>
              <ul style={{ fontSize: '11px', color: '#374151', lineHeight: '1.6', margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {proposal.exclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Project Conditions */}
        {proposal.projectConditions && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Project Conditions</h2>
          <p style={{ fontSize: '11px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
        )}

        {/* Additional Notes */}
        {proposal.additionalNotes && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Additional Notes</h2>
          <p style={{ fontSize: '11px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
        )}

        {/* About Sections */}
        {proposal.aboutSections.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.5rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>A Little About Us</h2>
          {proposal.aboutSections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < proposal.aboutSections.length - 1 ? '1.5rem' : 0 }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937', marginBottom: '0.15rem' }}>{section.label}</p>
              <p style={{ fontSize: '11px', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{section.text}</p>
            </div>
          ))}
        </div>
        )}

        {/* Terms */}
        {proposal.terms && proposal.terms.trim() && (
        <div>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '0.75rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Terms & Conditions</h2>
          <div style={{ fontSize: '10px', color: '#374151', lineHeight: '1.6', marginLeft: '1.25rem' }}>
            {proposal.terms.split('\n').map((term, idx) => (
              <p key={idx} style={{ marginBottom: '0.25rem' }}>
                <span style={{ color: bc, fontWeight: '600', marginRight: '0.5rem' }}>{idx + 1}.</span>
                {term.replace(/^\d+\.\s*/, '')}
              </p>
            ))}
          </div>
        </div>
        )}

        {/* Signature */}
        <div style={{ marginTop: '2rem', borderTop: `2px solid ${bc}`, paddingTop: '1.5rem' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', color: bc, marginBottom: '1rem', textTransform: 'uppercase', borderBottom: `2px solid ${bc}`, paddingBottom: '0.5rem' }}>Authorization & Acceptance</h2>
          {proposal.signatureDataUrl ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div>
                <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '2.5rem', objectFit: 'contain', objectPosition: 'left', display: 'block', marginBottom: '0.25rem' }} />
                <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.35rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937' }}>{proposal.signatureName || 'Client Signature'}</p>
                <p style={{ fontSize: '9px', color: '#6b7280', marginTop: '0.15rem' }}>Signed: {new Date(proposal.signedAt!).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <div>
                <div style={{ borderBottom: '2px solid #d1d5db', height: '2.5rem', marginBottom: '0.35rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937' }}>Company Representative</p>
                <p style={{ fontSize: '9px', color: '#6b7280', marginTop: '0.15rem' }}>Date: _______________</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div>
                <div style={{ borderBottom: '2px solid #d1d5db', height: '2.5rem', marginBottom: '0.35rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937' }}>Client Signature</p>
                <p style={{ fontSize: '9px', color: '#6b7280', marginTop: '0.15rem' }}>Date: _______________</p>
              </div>
              <div>
                <div style={{ borderBottom: '2px solid #d1d5db', height: '2.5rem', marginBottom: '0.35rem' }} />
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937' }}>Company Representative</p>
                <p style={{ fontSize: '9px', color: '#6b7280', marginTop: '0.15rem' }}>Date: _______________</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ COMPACT TEMPLATE ============
function CompactTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  return (
    <div className="pp-page pp-page-cover" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Brand-color banner header: title left, logo right */}
      <div style={{ padding: '1.75rem 2.5rem', backgroundColor: bc, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '-0.01em', lineHeight: '1.2', marginBottom: '0.25rem' }}>{proposal.projectTitle}</h1>
          {proposal.projectAddress && <p style={{ fontSize: '11px', opacity: 0.85, marginBottom: '0.15rem' }}>Project Location: {proposal.projectAddress}</p>}
          <p style={{ fontSize: '11px', opacity: 0.7 }}>{proposal.company}</p>
        </div>
        <ProposalLogo url={proposal.logoUrl} size={88} style={{ flexShrink: 0, marginLeft: '1rem', borderRadius: 10, border: '2px solid rgba(255,255,255,0.3)' }} />
      </div>

      {/* Compact content area — no large photo, data-focused */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1.5rem 2.5rem' }}>
        {/* Proposal number */}
        <p style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 'bold', color: bc, marginBottom: '1rem' }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>

        {/* Two-column contact fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: '0.35rem' }}>Prepared For</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>{proposal.clientContact}</p>
            <p style={{ fontSize: '12px', color: '#4b5563', marginTop: '2px' }}>{proposal.client}</p>
            {proposal.clientEmail && <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{proposal.clientEmail}</p>}
          </div>
          <div>
            <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: '0.35rem' }}>Submitted By</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>{proposal.estimatorName}</p>
            <p style={{ fontSize: '12px', color: '#4b5563', marginTop: '2px' }}>{proposal.company}</p>
          </div>
        </div>

        {/* Project details section */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9ca3af', marginBottom: '0.35rem' }}>Project Location</p>
          {proposal.clientAddressLine1 && <p style={{ fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>{proposal.clientAddressLine1}</p>}
          {proposal.clientAddressLine2 && <p style={{ fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>{proposal.clientAddressLine2}</p>}
        </div>

        {/* Scope text */}
        {proposal.scope && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.7' }}>{proposal.scope}</p>
          </div>
        )}

        {/* Compact summary table */}
        {proposal.categories.length > 0 && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden', marginBottom: '1.5rem' }}>
            {proposal.categories.map((cat, cIdx) => (
              <div key={cIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0.75rem', fontSize: '12px', borderBottom: '1px solid #e5e7eb', backgroundColor: cIdx % 2 === 0 ? '#f9fafb' : '#fff' }}>
                <span style={{ color: '#374151' }}>{cat.name}</span>
                <span style={{ fontWeight: '600', color: '#1f2937' }}>{cat.categoryTotal}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.625rem 0.75rem' }}>
              <div style={{ backgroundColor: bc, color: 'white', padding: '0.4rem 1.25rem', borderRadius: '4px', fontSize: '13px', fontWeight: '700' }}>
                Total: {proposal.finalTotal || proposal.total}
              </div>
            </div>
          </div>
        )}

        {/* Date info at bottom */}
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
          <span>Date: {proposal.date}</span>
          <span>Valid until {proposal.validUntil}</span>
        </div>
      </div>
    </div>
  );
}

function CompactTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, checkedTotal, displayTotal } = useOptionsTotal(proposal, checkedOptions);
  return (
    <div className="pp-page pp-page-inner" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '0.75rem 1.5rem', backgroundColor: bc, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ fontSize: '12px', fontWeight: '600' }}>{proposal.company}</p>
        <p style={{ fontSize: '10px', opacity: 0.8 }}>#{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Rev ${proposal.revisionCount}` : ''}</p>
      </div>

      {/* Content */}
      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flex: 1, fontSize: '11px', overflow: 'hidden' }}>
        {/* Scope */}
        {proposal.scope && proposal.scope.trim() && (
        <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
          <p style={{ fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>Scope of Work</p>
          <p style={{ color: '#374151', lineHeight: '1.5', fontSize: '10px' }}>{proposal.scope}</p>
        </div>
        )}

        {/* Project Details — categories with writeup descriptions */}
        <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
          <p style={{ fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem' }}>Project Details</p>
          {proposal.categories.map((cat, cIdx) => (
            <div key={cIdx} style={{ marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '10px', fontWeight: '700', color: bc, marginBottom: '0.25rem', textTransform: 'uppercase' }}>{cat.name}</p>
              {cat.groups.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '0.5rem' }}>
                  {group.name && <p style={{ fontSize: '9px', fontWeight: '700', color: '#1f2937', marginBottom: '0.15rem' }}>{group.name}</p>}
                  {group.items.map((item, iIdx) => (
                    <div key={iIdx} style={{ padding: '0.25rem 0.5rem', fontSize: '9px', color: '#374151', lineHeight: '1.5', borderBottom: '1px solid #e5e7eb' }}>
                      {item}
                    </div>
                  ))}
                </div>
              ))}
              {/* Category total */}
              <div style={{ paddingTop: '0.25rem', fontSize: '10px' }}>
                <span style={{ fontWeight: '700', color: bc }}>{cat.name} Total: </span>
                <span style={{ fontWeight: '700', color: '#1f2937' }}>{cat.categoryTotal}</span>
              </div>
              {/* Category assets */}
              {cat.assets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {cat.assets.map((asset, aIdx) => (
                    <div key={aIdx}>
                      <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '3px', display: 'block' }} />
                      {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.4' }}>{asset.description}</p>}
                      {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
                        <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          See Map — {proposal.pinLetters[asset.assetId]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* Optional Add-Ons */}
          <OptionsAddOnsSection options={options} checkedIds={checkedOptions} onToggle={toggleOption} brandColor={bc} />

          {/* Project Summary */}
          <div style={{ marginTop: '0.75rem' }}>
            <p style={{ fontSize: '10px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Project Summary</p>
            {proposal.categories.map((cat, cIdx) => (
              <div key={cIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0.5rem', fontSize: '10px', borderBottom: '1px solid #e5e7eb', backgroundColor: cIdx % 2 === 0 ? '#fafafa' : 'white' }}>
                <span style={{ color: '#374151' }}>{cat.name}</span>
                <span style={{ fontWeight: '600', color: '#1f2937' }}>{cat.categoryTotal}</span>
              </div>
            ))}
            <DiscountRow proposal={proposal} style={{ padding: '0.3rem 0.5rem', fontSize: '10px' }} />
            <div style={{ marginTop: '0.25rem', padding: '0.4rem 0.5rem', backgroundColor: bc, color: 'white', borderRadius: '2px', display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold' }}>
              <span>Final Base Bid Total</span>
              <span>{displayTotal}</span>
            </div>
            {checkedTotal > 0 && (
              <div style={{ fontSize: '9px', color: '#6b7280', fontStyle: 'italic', padding: '0.2rem 0.5rem' }}>Includes selected optional add-ons</div>
            )}
          </div>
        </div>

        {/* Inclusions & Exclusions */}
        {(proposal.inclusions.length > 0 || proposal.exclusions.length > 0) && (
        <div style={{ marginBottom: '0.75rem', fontSize: '9px' }}>
          {proposal.inclusions.length > 0 && (
            <div style={{ marginBottom: '0.5rem' }}>
              <p style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.2rem', textTransform: 'uppercase' }}>This Estimate Includes</p>
              <ul style={{ color: '#374151', lineHeight: '1.4', margin: 0, paddingLeft: '1rem', listStyleType: 'disc' }}>
                {proposal.inclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.15rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
          {proposal.exclusions.length > 0 && (
            <div>
              <p style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.2rem', textTransform: 'uppercase' }}>This Estimate Does Not Include</p>
              <ul style={{ color: '#374151', lineHeight: '1.4', margin: 0, paddingLeft: '1rem', listStyleType: 'disc' }}>
                {proposal.exclusions.map((item, idx) => <li key={idx} style={{ marginBottom: '0.15rem' }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Project Conditions */}
        {proposal.projectConditions && (
        <div style={{ marginBottom: '0.75rem', fontSize: '9px' }}>
          <p style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.2rem', textTransform: 'uppercase' }}>Project Conditions</p>
          <p style={{ color: '#374151', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
        )}

        {/* Additional Notes */}
        {proposal.additionalNotes && (
        <div style={{ marginBottom: '0.75rem', fontSize: '9px' }}>
          <p style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.2rem', textTransform: 'uppercase' }}>Additional Notes</p>
          <p style={{ color: '#374151', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
        )}

        {/* About Sections */}
        {proposal.aboutSections.length > 0 && (
        <div style={{ marginBottom: '0.75rem', fontSize: '9px' }}>
          <p style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.2rem', textTransform: 'uppercase' }}>A Little About Us</p>
          {proposal.aboutSections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < proposal.aboutSections.length - 1 ? '1rem' : 0 }}>
              <p style={{ fontWeight: '600', color: '#374151', marginBottom: '0.1rem' }}>{section.label}</p>
              <p style={{ color: '#374151', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{section.text}</p>
            </div>
          ))}
        </div>
        )}

        {/* Terms */}
        {proposal.terms && proposal.terms.trim() && (
        <div style={{ marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', fontSize: '9px' }}>
          <p style={{ fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>Terms</p>
          <div style={{ color: '#374151', lineHeight: '1.4' }}>
            {proposal.terms.split('\n').map((term, idx) => (
              <p key={idx} style={{ marginBottom: '0.2rem' }}>{term}</p>
            ))}
          </div>
        </div>
        )}

        {/* Signature */}
        {proposal.signatureDataUrl ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '9px' }}>
          <div>
            <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '2rem', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.15rem' }} />
            <div style={{ borderBottom: '1px solid #d1d5db', marginBottom: '0.25rem' }} />
            <p style={{ fontWeight: '600', color: '#1f2937' }}>{proposal.signatureName || 'Client Signature'}</p>
            <p style={{ color: '#9ca3af' }}>Signed: {proposal.signedAt ? new Date(proposal.signedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #d1d5db', height: '2rem', marginBottom: '0.25rem' }} />
            <p style={{ fontWeight: '600', color: '#1f2937' }}>Authorized Representative</p>
          </div>
        </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '9px' }}>
          <div>
            <div style={{ borderBottom: '1px solid #d1d5db', height: '1.5rem', marginBottom: '0.25rem' }} />
            <p style={{ fontWeight: '600', color: '#1f2937' }}>Client Signature</p>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #d1d5db', height: '1.5rem', marginBottom: '0.25rem' }} />
            <p style={{ fontWeight: '600', color: '#1f2937' }}>Authorized Representative</p>
          </div>
        </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '0.5rem 1.5rem', backgroundColor: bc, color: 'white', textAlign: 'center', fontSize: '10px', fontWeight: '600' }}>
        Thank you for your business!
      </div>
    </div>
  );
}

// ============ MAP PAGE (shared across all templates) ============
// ============ COVER LETTER PAGE (shared across all templates) ============
function CoverLetterPage({ proposal }: { proposal: Proposal }): React.ReactElement | null {
  if (!proposal.coverLetter) return null;
  const bc = proposal.brandColor;
  return (
    <div className="pp-page pp-page-inner" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Brand header */}
      <div style={{ backgroundColor: bc, padding: '0.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ProposalLogo url={proposal.logoUrl} size={28} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: 'white' }}>{proposal.company}</span>
        </div>
        <span style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</span>
      </div>

      {/* Cover letter content */}
      <div style={{ padding: '2rem 2.5rem', flex: 1 }}>
        <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
          Letter of Introduction
        </h2>
        <div style={{ height: '3px', width: '40px', backgroundColor: bc, borderRadius: '2px', marginBottom: '1.5rem' }} />

        <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
          {proposal.coverLetter}
        </p>

        {/* Estimator sign-off */}
        <div style={{ marginTop: '2.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.25rem' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>{proposal.estimatorName}</p>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.company}</p>
          {proposal.phone && <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.phone}</p>}
          {proposal.email && <p style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.email}</p>}
        </div>
      </div>
    </div>
  );
}

function MapPage({ proposal }: { proposal: Proposal }): React.ReactElement | null {
  if (!proposal.mapAssets || proposal.mapAssets.length === 0) return null;
  const bc = proposal.brandColor;
  return (
    <>
      {proposal.mapAssets.map((asset, idx) => (
        <div key={idx} className="pp-page pp-page-inner" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Thin brand header */}
          <div style={{ backgroundColor: bc, padding: '0.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ProposalLogo url={proposal.logoUrl} size={28} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'white' }}>{proposal.company}</span>
            </div>
            <span style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</span>
          </div>

          {/* Section label */}
          <div style={{ padding: '1.25rem 2rem 0.75rem' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
              {asset.caption || 'Project Map'}
            </h2>
            <div style={{ height: '3px', width: '40px', backgroundColor: bc, borderRadius: '2px', marginTop: '0.5rem' }} />
          </div>

          {/* Full-width map image */}
          <div style={{ flex: 1, padding: '0 2rem 2rem', display: 'flex', flexDirection: 'column' }}>
            <img
              src={asset.url}
              alt={asset.caption || 'Project Map'}
              style={{ width: '100%', flex: 1, objectFit: 'contain', objectPosition: 'top center', borderRadius: '6px', display: 'block', minHeight: 0 }}
            />
            {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.4' }}>{asset.description}</p>}
            {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
              <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                See Map — {proposal.pinLetters[asset.assetId]}
              </p>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

// ============ ADDENDUM PAGE (shared across all templates) ============
function AddendumPage({ proposal }: { proposal: Proposal }): React.ReactElement | null {
  if (!proposal.addendumAssets || proposal.addendumAssets.length === 0) return null;
  const bc = proposal.brandColor;
  return (
    <>
      {proposal.addendumAssets.map((asset, idx) => (
        <div key={idx} className="pp-page pp-page-inner" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Thin brand header */}
          <div style={{ backgroundColor: bc, padding: '0.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ProposalLogo url={proposal.logoUrl} size={28} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'white' }}>{proposal.company}</span>
            </div>
            <span style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</span>
          </div>

          {/* Section label */}
          <div style={{ padding: '1.25rem 2rem 0.75rem' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
              Attachments{proposal.addendumAssets.length > 1 ? ` — ${idx + 1} of ${proposal.addendumAssets.length}` : ''}
            </h2>
            {asset.caption && (
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.35rem' }}>{asset.caption}</p>
            )}
            <div style={{ height: '3px', width: '40px', backgroundColor: bc, borderRadius: '2px', marginTop: '0.5rem' }} />
          </div>

          {/* Full-width image */}
          <div style={{ flex: 1, padding: '0 2rem 2rem', display: 'flex', flexDirection: 'column' }}>
            <img
              src={asset.url}
              alt={asset.caption || 'Attachment'}
              style={{ width: '100%', flex: 1, objectFit: 'cover', borderRadius: '6px', display: 'block', minHeight: 0 }}
            />
            {asset.description && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.4' }}>{asset.description}</p>}
            {asset.assetId && proposal.pinLetters?.[asset.assetId] && (
              <p style={{ fontSize: '11px', color: '#EF5B6C', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                See Map — {proposal.pinLetters[asset.assetId]}
              </p>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

// ============ UNIT PRICE PROPOSAL BODY (stripped-down, shared) ============
function UnitPriceProposalBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const items = proposal.unitPriceItems || [];
  const grandTotal = items.reduce((sum, r) => sum + r.total, 0);
  const fmt = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="pp-page pp-page-inner">
      {/* Header bar */}
      <div style={{ backgroundColor: bc, height: '6px' }} />
      <div style={{ padding: '1.25rem 2rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ProposalLogo url={proposal.logoUrl} size={32} />
          <div>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{proposal.company}</p>
            <p style={{ fontSize: '10px', color: '#9ca3af', margin: 0 }}>{proposal.address}</p>
          </div>
        </div>
        <p style={{ fontSize: '10px', color: '#9ca3af' }}>Proposal #{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''}</p>
      </div>

      {/* Content */}
      <div style={{ padding: '2rem' }}>
        {/* Project info header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: bc }} />
            <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Unit Price Schedule</h2>
          </div>

          {/* Client / project info row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', padding: '1rem', backgroundColor: '#fafafa', borderRadius: '6px', marginBottom: '1.5rem' }}>
            <div>
              <p style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: '0.25rem' }}>Prepared For</p>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{proposal.clientContact}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{proposal.client}</p>
              {proposal.clientAddressLine1 && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.25rem' }}>{proposal.clientAddressLine1}</p>}
              {proposal.clientAddressLine2 && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.1rem' }}>{proposal.clientAddressLine2}</p>}
              {proposal.clientEmail && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.1rem' }}>{proposal.clientEmail}</p>}
            </div>
            <div>
              <p style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: '0.25rem' }}>Project</p>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{proposal.projectTitle}</p>
              {proposal.projectAddress && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.25rem' }}>{proposal.projectAddress}</p>}
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '0.25rem' }}>{proposal.date}</p>
            </div>
          </div>
        </div>

        {/* Unit Price Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'left' }}>Item</th>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'left' }}>Description</th>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'left' }}>Unit Type</th>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'right' }}>Unit Price</th>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'right' }}>Qty</th>
              <th style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', padding: '0.75rem 0.75rem', textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white' }}>
                <td style={{ padding: '0.75rem', fontSize: '12px', fontWeight: '500', color: '#1f2937' }}>{row.item}</td>
                <td style={{ padding: '0.75rem', fontSize: '12px', color: '#374151' }}>{row.description}</td>
                <td style={{ padding: '0.75rem', fontSize: '12px', color: '#374151' }}>{row.unitType}</td>
                <td style={{ padding: '0.75rem', fontSize: '12px', color: '#374151', textAlign: 'right' }}>{fmt(row.unitPrice)}</td>
                <td style={{ padding: '0.75rem', fontSize: '12px', color: '#374151', textAlign: 'right' }}>{row.quantity.toLocaleString('en-US')}</td>
                <td style={{ padding: '0.75rem', fontSize: '12px', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>{fmt(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ padding: '0.75rem', fontSize: '13px', fontWeight: '700', color: 'white', backgroundColor: bc, textAlign: 'right' }}>Total</td>
              <td style={{ padding: '0.75rem', fontSize: '13px', fontWeight: '700', color: 'white', backgroundColor: bc, textAlign: 'right' }}>{fmt(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Signature */}
        <div style={{ marginBottom: '1rem', marginTop: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: bc }} />
            <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Acceptance & Signature</h2>
          </div>
          {proposal.signatureDataUrl ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem' }}>
                <div>
                  <img src={proposal.signatureDataUrl} alt="Client Signature" style={{ height: '3rem', maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.25rem' }} />
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>{proposal.signatureName || 'Client Signature'}</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Signed: {proposal.signedAt ? new Date(proposal.signedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '3rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2rem', lineHeight: '1.5' }}>By signing below, you accept the terms and conditions of this proposal and authorize {proposal.company} to proceed with the described work.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem' }}>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Client Signature</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
                <div>
                  <div style={{ borderBottom: '2px solid #d1d5db', marginBottom: '0.5rem', height: '2.5rem' }} />
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#374151' }}>Company Representative</p>
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '0.25rem' }}>Date: _______________</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer bar */}
      <div style={{ marginTop: 'auto', backgroundColor: bc, padding: '0.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ fontSize: '10px', color: 'white', fontWeight: '600' }}>Thank you for your business!</p>
        <p style={{ fontSize: '10px', color: 'white', opacity: 0.7 }}>{proposal.company}</p>
      </div>
    </div>
  );
}

// ─── MONOLITH TEMPLATE ────────────────────────────────────────────────────────

const MONO_PAPER = '#FAF8F4';
const MONO_INK   = '#0F0F10';
const MONO_DIM   = '#6B6B70';
const MONO_LINE  = '#E4E2DD';
const MONO_FONT  = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

function MonolithSectionHead({ num, kicker, title, bc }: { num: string; kicker: string; title: string; bc: string }): React.ReactElement {
  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', color: bc, fontWeight: 600, letterSpacing: '0.2em' }}>{num}</span>
        <span style={{ fontSize: '10px', letterSpacing: '0.32em', textTransform: 'uppercase', color: MONO_DIM }}>{kicker}</span>
      </div>
      <h2 style={{ margin: 0, fontSize: '36px', lineHeight: 1.0, fontWeight: 300, letterSpacing: '-0.02em', color: MONO_INK, fontFamily: MONO_FONT }}>{title}</h2>
      <div style={{ marginTop: '18px', height: '1px', background: MONO_LINE }} />
    </div>
  );
}

function MonolithInnerPage({ children, proposal, pageNum }: { children: React.ReactNode; proposal: Proposal; pageNum: string }): React.ReactElement {
  return (
    <div className="pp-page pp-page-inner" style={{ background: MONO_PAPER, color: MONO_INK, fontFamily: MONO_FONT, padding: '64px 72px 56px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      {children}
      <div style={{ marginTop: 'auto', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '9.5px', letterSpacing: '0.18em', color: MONO_DIM, textTransform: 'uppercase', borderTop: `1px solid ${MONO_LINE}` }}>
        <span>{proposal.company} · Monolith Series</span>
        <span>{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Rev ${proposal.revisionCount}` : ''} · {pageNum}</span>
      </div>
    </div>
  );
}

function MonolithTemplateCover({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  return (
    <div className="pp-page pp-page-cover" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', background: MONO_PAPER, color: MONO_INK, fontFamily: MONO_FONT, overflow: 'hidden' }}>
      {/* Left content column */}
      <div style={{ padding: '64px 56px 56px', display: 'flex', flexDirection: 'column' }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '18px', borderBottom: `1px solid ${MONO_LINE}` }}>
          {proposal.logoUrl
            ? <img src={proposal.logoUrl} alt={proposal.company} style={{ height: '60px', display: 'block', objectFit: 'contain' }} />
            : <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{proposal.company}</div>}
          <div style={{ textAlign: 'right', fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>
            Vol. {proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Rev ${proposal.revisionCount}` : ''}<br />
            <span style={{ color: MONO_INK }}>{proposal.date}</span>
          </div>
        </div>

        {/* Hero text */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: '9.5px', letterSpacing: '0.36em', textTransform: 'uppercase', color: bc, marginBottom: '32px' }}>— Proposal of Works</div>
          <h1 style={{ margin: 0, fontSize: '72px', lineHeight: 0.92, fontWeight: 300, letterSpacing: '-0.025em', color: MONO_INK, maxWidth: '520px' }}>
            {proposal.projectTitle}
          </h1>
          {proposal.projectAddress && <p style={{ margin: '20px 0 0', fontSize: '13px', lineHeight: 1.5, color: MONO_DIM, maxWidth: '440px' }}>Project Location: {proposal.projectAddress}</p>}
          <div style={{ marginTop: '36px', width: '48px', height: '2px', background: bc }} />
          <p style={{ margin: '28px 0 0', fontSize: '13px', lineHeight: 1.65, color: MONO_DIM, maxWidth: '440px' }}>
            Prepared for {proposal.client} by the estimating team at {proposal.company}.
            This document outlines the proposed scope, schedule and investment for the works described.
          </p>
        </div>

        {/* Footer contact blocks */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', paddingTop: '28px', borderTop: `1px solid ${MONO_LINE}` }}>
          <div>
            <div style={{ fontSize: '9.5px', letterSpacing: '0.28em', textTransform: 'uppercase', color: MONO_DIM, marginBottom: '14px' }}>Prepared For</div>
            <div style={{ fontSize: '18px', fontWeight: 500, color: MONO_INK, marginBottom: '4px' }}>{proposal.clientContact}</div>
            <div style={{ fontSize: '13px', color: MONO_INK, marginBottom: '8px' }}>{proposal.client}</div>
            <div style={{ fontSize: '11.5px', lineHeight: 1.55, color: MONO_DIM }}>
              {proposal.clientAddressLine1 && <>{proposal.clientAddressLine1}<br /></>}
              {proposal.clientAddressLine2 && <>{proposal.clientAddressLine2}<br /></>}
              {proposal.clientPhone}
              {proposal.clientEmail && <><br />{proposal.clientEmail}</>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '9.5px', letterSpacing: '0.28em', textTransform: 'uppercase', color: MONO_DIM, marginBottom: '14px' }}>Submitted By</div>
            <div style={{ fontSize: '18px', fontWeight: 500, color: MONO_INK, marginBottom: '4px' }}>{proposal.estimatorName}</div>
            <div style={{ fontSize: '13px', color: MONO_INK, marginBottom: '8px' }}>{proposal.company}</div>
            <div style={{ fontSize: '11.5px', lineHeight: 1.55, color: MONO_DIM }}>
              {(() => { const p = (proposal.address || '').split(/,\s*/); return p.length > 1 ? <>{p[0]}<br />{p.slice(1).join(', ')}</> : <>{proposal.address}</> })()}
              <br />{proposal.phone}
            </div>
          </div>
        </div>
      </div>

      {/* Right photo strip */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {proposal.coverPhoto ? (
          <>
            <img src={proposal.coverPhoto} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
            {proposal.headerTintEnabled && <div style={{ position: 'absolute', inset: 0, background: bc, opacity: 0.55 }} />}
          </>
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: bc }} />
        )}
      </div>
    </div>
  );
}

function MonolithTemplateBody({ proposal }: { proposal: Proposal }): React.ReactElement {
  const bc = proposal.brandColor;
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());
  const toggleOption = (id: string) => setCheckedOptions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { options, displayTotal } = useOptionsTotal(proposal, checkedOptions);

  // Page 2 — Scope & Pricing
  const scopePage = (
    <MonolithInnerPage proposal={proposal} pageNum="02 / 05">
      <MonolithSectionHead num="I." kicker="Scope of Work" title="Project Details" bc={bc} />
      {proposal.scope && (
        <p style={{ margin: '0 0 32px', fontSize: '13px', lineHeight: 1.65, color: MONO_INK, maxWidth: '600px' }}>{proposal.scope}</p>
      )}
      {proposal.categories.map((cat, ci) => {
        const lineItems = cat.groups.flatMap(g => g.items);
        const catTotal = parseFloat(String(cat.categoryTotal).replace(/[^0-9.]/g, '')) || 0;
        const fmt = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return (
          <div key={ci} style={{ paddingBottom: '22px', marginBottom: '22px', borderBottom: `1px solid ${MONO_LINE}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '14px' }}>
              <div>
                <div style={{ width: '32px', height: '2px', background: bc, marginBottom: '10px' }} />
                <div style={{ fontSize: '22px', fontWeight: 400, letterSpacing: '-0.01em', color: MONO_INK }}>{cat.name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '22px', fontWeight: 500, letterSpacing: '-0.01em', color: MONO_INK }}>{fmt(catTotal)}</div>
              </div>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {lineItems.map((li, j) => (
                <li key={j} style={{ display: 'grid', gridTemplateColumns: '32px 1fr', fontSize: '12.5px', lineHeight: 1.6, color: MONO_INK, paddingTop: '4px' }}>
                  <span style={{ color: bc, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{String(j + 1).padStart(2, '0')}</span>
                  <span>{li}</span>
                </li>
              ))}
            </ul>
            {cat.assets && cat.assets.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '54px' }}>
                {cat.assets.map((asset, aIdx) => (
                  <div key={aIdx}>
                    <img src={asset.url} alt={asset.caption || ''} style={{ width: '100%', borderRadius: '4px', display: 'block' }} />
                    {asset.description && <p style={{ margin: '4px 0 0', fontSize: '11px', color: MONO_DIM, lineHeight: 1.4 }}>{asset.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {/* Optional Add-Ons */}
      {options.length > 0 && (
        <div style={{ marginTop: '18px', marginBottom: '18px' }}>
          <div style={{ width: '32px', height: '2px', background: bc, marginBottom: '10px' }} />
          <div style={{ fontSize: '22px', fontWeight: 400, letterSpacing: '-0.01em', color: MONO_INK, marginBottom: '14px' }}>Optional Add-Ons</div>
          {options.map((opt) => (
            <label key={opt.id} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', alignItems: 'baseline', gap: '12px', padding: '10px 0', borderBottom: `1px dashed ${MONO_LINE}`, cursor: 'pointer' }}>
              <input type="checkbox" checked={checkedOptions.has(opt.id)} onChange={() => toggleOption(opt.id)} style={{ accentColor: bc, marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '13px', color: MONO_INK }}>{opt.name}</div>
                {opt.description && <div style={{ fontSize: '11px', color: MONO_DIM, marginTop: '2px', lineHeight: 1.5 }}>{opt.description}</div>}
              </div>
              <div style={{ fontSize: '13px', color: MONO_INK, fontVariantNumeric: 'tabular-nums' }}>{fmtDollars(opt.cost)}</div>
            </label>
          ))}
        </div>
      )}

      {/* Project Summary */}
      <div style={{ marginTop: '18px' }}>
        <div style={{ width: '32px', height: '2px', background: bc, marginBottom: '10px' }} />
        <div style={{ fontSize: '22px', fontWeight: 400, letterSpacing: '-0.01em', color: MONO_INK, marginBottom: '14px' }}>Project Summary</div>
        <div style={{ padding: '4px 0' }}>
          {proposal.categories.map((cat, ci) => {
            const fmt = (s: string) => s.startsWith('$') ? s : `$${s}`;
            return (
              <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '8px 0', borderBottom: `1px dashed ${MONO_LINE}` }}>
                <span style={{ color: MONO_INK }}>{cat.name}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: MONO_INK }}>{fmt(cat.categoryTotal)}</span>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: '14px', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', letterSpacing: '0.24em', textTransform: 'uppercase', color: MONO_DIM }}>Final Base Bid</span>
            <span style={{ fontSize: '28px', fontWeight: 500, color: bc, fontVariantNumeric: 'tabular-nums' }}>{displayTotal}</span>
          </div>
        </div>
      </div>
    </MonolithInnerPage>
  );

  // Page 3 — Includes + Excludes + Acceptance (combined)
  const excludesPage = (
    <MonolithInnerPage proposal={proposal} pageNum="03 / 05">
      {proposal.inclusions.length > 0 && (
        <>
          <MonolithSectionHead num="II." kicker="Inclusions" title="What this estimate includes" bc={bc} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '36px', marginBottom: '40px' }}>
            {proposal.inclusions.map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', alignItems: 'baseline', padding: '14px 0', borderBottom: `1px solid ${MONO_LINE}`, fontSize: '13px', color: MONO_INK }}>
                <span style={{ fontSize: '14px', color: bc, fontWeight: 700 }}>+</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <MonolithSectionHead num={proposal.inclusions.length > 0 ? 'III.' : 'II.'} kicker="Exclusions & Acceptance" title="Not included · Acceptance" bc={bc} />
      {proposal.exclusions.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '36px', marginBottom: '40px' }}>
          {proposal.exclusions.map((item, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${MONO_LINE}`, fontSize: '12px', color: MONO_INK, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '1px', background: MONO_DIM, flexShrink: 0 }} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      )}
      {proposal.projectConditions && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.32em', textTransform: 'uppercase', color: MONO_DIM, marginBottom: '10px' }}>Project Conditions</div>
          <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.65, color: MONO_INK, whiteSpace: 'pre-wrap' }}>{proposal.projectConditions}</p>
        </div>
      )}
      {proposal.additionalNotes && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.32em', textTransform: 'uppercase', color: MONO_DIM, marginBottom: '10px' }}>Additional Notes</div>
          <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.65, color: MONO_INK, whiteSpace: 'pre-wrap' }}>{proposal.additionalNotes}</p>
        </div>
      )}
      {proposal.terms && proposal.terms.trim() && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.32em', textTransform: 'uppercase', color: MONO_DIM, marginBottom: '10px' }}>Terms & Conditions</div>
          <div style={{ fontSize: '11.5px', lineHeight: 1.65, color: MONO_INK }}>
            {proposal.terms.split('\n').map((line, i) => <p key={i} style={{ margin: '0 0 6px' }}>{line}</p>)}
          </div>
        </div>
      )}
    </MonolithInnerPage>
  );

  // Page 4 — Acceptance
  const acceptancePage = (
    <MonolithInnerPage proposal={proposal} pageNum="04 / 05">
      <MonolithSectionHead num="IV." kicker="Acceptance" title="Authorization & Signature" bc={bc} />
      <p style={{ margin: '0 0 40px', fontSize: '12.5px', lineHeight: 1.65, color: MONO_INK, maxWidth: '600px' }}>
        By signing below, the client accepts the terms and conditions of this proposal and authorizes {proposal.company} to proceed with the described work.
      </p>
      {proposal.signatureDataUrl ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
          <div>
            <img src={proposal.signatureDataUrl} alt="Signature" style={{ height: '56px', maxWidth: '100%', objectFit: 'contain' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <div style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>Client Signature</div>
              <div style={{ fontSize: '10px', color: MONO_DIM }}>{proposal.signedAt}</div>
            </div>
          </div>
          <div>
            <div style={{ height: '56px', borderBottom: `1px solid ${MONO_INK}` }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <div style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>Company Representative</div>
              <div style={{ fontSize: '10px', color: MONO_DIM }}>Date</div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
          {['Client Signature', 'Company Representative'].map(label => (
            <div key={label}>
              <div style={{ height: '56px', borderBottom: `1px solid ${MONO_INK}` }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                <div style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>{label}</div>
                <div style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>Date</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </MonolithInnerPage>
  );

  // Page 5 — Closing
  const closePage = (
    <div className="pp-page pp-page-inner" style={{ background: MONO_PAPER, color: MONO_INK, fontFamily: MONO_FONT, padding: '64px 72px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>{proposal.company}</div>
        <div style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: MONO_DIM }}>{proposal.proposalNumber}{(proposal.revisionCount ?? 0) > 0 ? ` · Rev ${proposal.revisionCount}` : ''}</div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: '11px', color: bc, letterSpacing: '0.36em', textTransform: 'uppercase', marginBottom: '28px' }}>— Closing</div>
        <h1 style={{ margin: 0, fontSize: '80px', lineHeight: 0.92, fontWeight: 300, letterSpacing: '-0.03em', maxWidth: '580px', color: MONO_INK }}>
          Thank you for your<br />consideration.
        </h1>
        <div style={{ marginTop: '40px', width: '64px', height: '2px', background: bc }} />
        <p style={{ margin: '32px 0 0', fontSize: '13.5px', lineHeight: 1.7, color: MONO_DIM, maxWidth: '480px' }}>
          We're grateful for the opportunity to put a proposal in front of you. Should any element of this scope require revision, we'll iterate gladly. — {proposal.estimatorName}
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', gap: '28px', paddingTop: '24px', borderTop: `1px solid ${MONO_LINE}`, fontSize: '11px', color: MONO_DIM }}>
        <div><span style={{ color: MONO_INK }}>{proposal.phone}</span></div>
        <div><span style={{ color: MONO_INK }}>{proposal.email}</span></div>
        <div><span style={{ color: MONO_INK }}>{proposal.website}</span></div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}><span style={{ color: MONO_INK }}>{proposal.address}</span></div>
      </div>
    </div>
  );

  return (
    <>
      {scopePage}
      {excludesPage}
      {acceptancePage}
      {closePage}
    </>
  );
}

// ─── END MONOLITH TEMPLATE ─────────────────────────────────────────────────────

interface TemplateRendererProps {
  template: string;
  proposal: Proposal;
}

export function TemplateRenderer({ template, proposal }: TemplateRendererProps): React.ReactElement {
  // Unit pricing mode: stripped-down proposal (no cover, no project details, no scope)
  if (proposal.isUnitPricing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
        <UnitPriceProposalBody proposal={proposal} />
        <MapPage proposal={proposal} />
        <AddendumPage proposal={proposal} />
      </div>
    );
  }

  switch (template) {
    case 'classic':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <ClassicTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <ClassicTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    case 'modern':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <ModernTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <ModernTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    case 'bold':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <BoldTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <BoldTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    case 'technical':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <TechnicalTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <TechnicalTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    case 'compact':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <CompactTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <CompactTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    case 'monolith':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <MonolithTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <MonolithTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
    default:
      // Any unrecognized slug renders the Classic template — the SAME fallback the
      // backend PDF uses (buildCoverPage default → buildClassicCover). This guarantees
      // an unknown/legacy templateSlug never shows the client a broken page, and that
      // the web and PDF fall back to the identical layout. Selectable templates are
      // additionally CI-guarded to have a real renderer on both sides
      // (section-totals.guard.test.ts → template parity).
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
          <ClassicTemplateCover proposal={proposal} />
          <CoverLetterPage proposal={proposal} />
          <ClassicTemplateBody proposal={proposal} />
          <MapPage proposal={proposal} />
          <AddendumPage proposal={proposal} />
        </div>
      );
  }
}

export function ProposalPreviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [template, setTemplate] = useState<string>(searchParams.get('template') || 'modern');
  const mode = searchParams.get('mode');
  const isPreviewOnly = mode === 'preview';
  const isSettingsPreview = mode === 'settings-preview';
  const backTo = searchParams.get('from') || '/proposals';

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [proposalRecordId, setProposalRecordId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<{ status?: number; message: string } | null>(null);

  // Extract estimate ID from query params or resolve from route-param proposal ID
  const directEstimateId = searchParams.get('estimateId');
  const fromParam = searchParams.get('from') || '';
  const estimateIdMatch = fromParam.match(/\/estimates\/([^/?]+)/);
  const estimateIdFromParams = directEstimateId || (estimateIdMatch ? estimateIdMatch[1] : null);
  const [resolvedEstimateId, setResolvedEstimateId] = useState<string | null>(estimateIdFromParams);
  // Use resolved ID (may be set asynchronously when navigated via /proposals/:id)
  const estimateId = resolvedEstimateId;

  // Label mapping for about sections
  const ABOUT_LABELS: Record<string, string> = {
    aboutCompany: 'Our Company',
    mission: 'Our Mission',
    whoWeServe: 'Who We Serve',
    reasonsToChoose: 'Why Us?',
    termsConditions: 'Terms and Conditions',
  };

  useEffect(() => {
    // Helper: parse aboutUs JSON from settings
    // termsConditions is excluded from aboutSections — it renders in its own "Terms & Conditions" section
    const parseAboutSections = (settings: any): { label: string; text: string }[] => {
      if (!settings.aboutUs) return [];
      try {
        const parsed = JSON.parse(settings.aboutUs);
        return Object.entries(parsed)
          .filter(([key, val]: [string, any]) => key !== 'termsConditions' && val && val.includeInProposal && val.text?.trim())
          .map(([key, val]: [string, any]) => ({
            label: ABOUT_LABELS[key] || key,
            text: val.text,
          }));
      } catch {
        if (settings.aboutUs.trim()) {
          return [{ label: 'About Us', text: settings.aboutUs }];
        }
        return [];
      }
    };

    // Helper: extract terms from aboutUs JSON (only if includeInProposal is checked)
    const parseTerms = (settings: any): string => {
      if (!settings.aboutUs) return '';
      try {
        const parsed = JSON.parse(settings.aboutUs);
        const tc = parsed.termsConditions;
        if (tc && tc.includeInProposal && tc.text?.trim()) return tc.text;
        return '';
      } catch {
        return '';
      }
    };

    // Helper: build company fields from settings
    const buildCompanyFields = (settings: any) => ({
      brandColor: settings.primaryColor || sampleProposal.brandColor,
      logoUrl: settings.logoUrl || '',
      coverPhoto: settings.coverPhotoUrl || '/images/covers/cover-1.jpg',
      headerTintEnabled: settings.headerTintEnabled ?? true,
      company: settings.companyName || sampleProposal.company,
      phone: settings.companyPhone || sampleProposal.phone,
      email: settings.companyEmail || sampleProposal.email,
      website: settings.companyWebsite || sampleProposal.website,
      address: [settings.companyAddress, settings.companyCity, settings.companyState, settings.companyZip].filter(Boolean).join(', ') || sampleProposal.address,
    });

    // Helper: format currency
    const fmt = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const loadData = async () => {
      try {
        const settings = await companyService.getSettings();
        const aboutSections = parseAboutSections(settings);
        const termsText = parseTerms(settings);
        const companyFields = buildCompanyFields(settings);

        // If navigated via /proposals/:id (no query params), resolve the estimate ID from the proposal record
        let effectiveEstimateId = estimateId;
        if (!effectiveEstimateId && id) {
          try {
            const proposalById = await proposalsService.getById(id);
            if (proposalById?.estimateId) {
              effectiveEstimateId = proposalById.estimateId;
              setResolvedEstimateId(effectiveEstimateId);
              setProposalRecordId(proposalById.id);
              if (proposalById.templateSlug) setTemplate(proposalById.templateSlug);
            }
          } catch (err: any) {
            // Real proposal that couldn't be loaded — show an error rather than fake sample data
            const status = err?.response?.status ?? err?.status;
            setLoadError({
              status,
              message:
                status === 403
                  ? "You don't have permission to view this proposal."
                  : status === 404
                    ? 'Proposal not found.'
                    : 'Failed to load this proposal.',
            });
            return;
          }
        }

        // If we have an estimate ID, try to load real proposal + estimate data
        if (effectiveEstimateId) {
          try {
            const [proposalRecord, estimate, inclItems, exclItems, estimateAssets, mapPins, lchRows] = await Promise.all([
              proposalsService.getByEstimate(effectiveEstimateId),
              estimatesService.getById(effectiveEstimateId),
              exclusionsService.listByType('inclusion'),
              exclusionsService.listByType('exclusion'),
              assetsService.list(effectiveEstimateId).catch(() => [] as EstimateAsset[]),
              estimatesService.getMapPins(effectiveEstimateId).catch(() => []),
              lchService.getEstimateLchData(effectiveEstimateId).catch(() => []),
            ]);

            // Build pin letter lookup: assetId → letter (A, B, C...)
            const pinLetterMap: Record<string, string> = {};
            mapPins.forEach((pin, idx) => {
              if (pin.photoAssetId) {
                let result = '';
                let n = idx;
                do {
                  result = String.fromCharCode(65 + (n % 26)) + result;
                  n = Math.floor(n / 26) - 1;
                } while (n >= 0);
                pinLetterMap[pin.photoAssetId] = result;
              }
            });

            // If a proposal record exists, use its stamped template
            if (proposalRecord) {
              setProposalRecordId(proposalRecord.id);
              if (proposalRecord.templateSlug) {
                setTemplate(proposalRecord.templateSlug);
              }
              // If signed, resolve the first active invoice for the "View Invoice" link
              if (proposalRecord.status === 'signed') {
                try {
                  const invs = await invoicesService.getByProposal(proposalRecord.id);
                  const active = invs.find((inv: any) => inv.status !== 'void');
                  if (active) setInvoiceId(active.id);
                } catch { /* no invoices yet — fine */ }
              }
            }

            // ── Frozen-snapshot gating ────────────────────────────────────
            // A sent proposal is a contract: its financials/scope must match
            // exactly what the client sees on the signature page and PDF. The
            // backend freezes an estimate snapshot at send time and overlays it
            // onto `proposalRecord.estimate` on read, so for a sent proposal
            // that object carries the FROZEN financials + lineItems. For drafts
            // (and bare-estimate previews with no proposal) there is no snapshot,
            // so we render the LIVE estimate and the estimator's pre-send edits
            // show through unchanged.
            //
            // `effectiveEstimate` is used ONLY for the rendered proposal output
            // (financials, discounts, line-item/category aggregation, scope).
            // Client/job-site/estimator identity fields are intentionally read
            // from the LIVE `estimate` below — they are NOT part of the frozen
            // snapshot (see SNAPSHOT_SCALAR_FIELDS in proposal-snapshot.ts) and
            // reading them off the snapshot would render them blank.
            const isFrozenProposal = Boolean(proposalRecord?.sentToClientAt && proposalRecord?.estimate);
            const effectiveEstimate: any = isFrozenProposal ? (proposalRecord as any).estimate : estimate;

            // Build real proposal from estimate data
            const lineItems: LineItem[] = (effectiveEstimate.lineItems || []).map((li: any) => ({
              desc: li.description,
              qty: li.quantity.toLocaleString('en-US'),
              unit: li.unit,
              rate: fmt(li.rate),
              total: fmt(li.lineTotal),
            }));

            // Build categories from writeup data (scopeData) + line item totals
            // The writeup tab contains the descriptive scope items shown in the proposal
            // Line items are only used for category totals (not displayed individually)
            const PROJECT_SECTIONS: { key: string; label: string }[] = [
              { key: 'asphalt', label: 'Asphalt' },
              { key: 'concrete', label: 'Concrete' },
              { key: 'grading', label: 'Grading' },
              { key: 'seal-coat', label: 'Seal Coat' },
              { key: 'striping', label: 'Striping' },
            ];

            const allItems = effectiveEstimate.lineItems || [];

            // Build H&D cost per section from LCH calculator data.
            // For SENT (frozen) proposals, the source must be the frozen
            // `effectiveEstimate.lchData` snapshot — the same field the PDF and
            // signature page read — so category totals match what the client sees
            // even after a post-send edit. For drafts, keep using the LIVE
            // `lchRows` fetched from `lchService.getEstimateLchData` above.
            // Both paths use the identical `(row.section as string) || '_general'`
            // section-key fallback so frozen and live produce identically-keyed maps.
            // H&D per section — canonical parse/bucket shared with every other surface.
            // Frozen (sent) proposals read the snapshot's lchData; drafts use the live fetch.
            const lchSourceRows = isFrozenProposal
              ? parseLchData(effectiveEstimate.lchData)
              : (lchRows || []);
            const lchBySection = lchCostBySection(lchSourceRows);
            // Per-service subcontractors fold into their service's category total.
            const subcontractorsBySection = subcontractorCostBySection(
              parseSubcontractors((effectiveEstimate as any).subcontractorsData),
            );

            // Helper: render a writeup row's content array to a plain string
            const renderRowContent = (content: any[]): string => {
              return content.map((item: any) => {
                if (typeof item === 'string') return item;
                if (item.placeholder === 'surface') return '';
                // It's a field object { value, size, placeholder }
                const val = item.value || item.placeholder || '___';
                return fmtWithCommas(val);
              }).join('');
            };

            // Parse saved writeup groups from scopeData, or build defaults from projectTypes
            let scopeGroups: any[] = [];
            if (effectiveEstimate.scopeData) {
              try {
                const parsed = JSON.parse(effectiveEstimate.scopeData);
                if (Array.isArray(parsed) && parsed.length > 0) scopeGroups = parsed;
              } catch { /* ignore parse errors */ }
            }
            // Fallback: build default writeup groups from projectTypes if no saved data
            if (scopeGroups.length === 0) {
              let ptArr: string[] = [];
              try { ptArr = JSON.parse(effectiveEstimate.projectTypes || '[]'); } catch { /* ignore */ }
              scopeGroups = buildDefaultWriteupGroups(ptArr);
            }

            // Build asset lookup by category
            const assetsByCategory: Record<string, CategoryAsset[]> = {};
            for (const asset of estimateAssets) {
              const cat = asset.category || 'map';
              if (!assetsByCategory[cat]) assetsByCategory[cat] = [];
              // Use clean label for map assets instead of raw filename
              const caption = cat === 'map' ? `Project Map${estimateAssets.filter(a => (a.category || 'map') === 'map').length > 1 ? ` ${assetsByCategory[cat].length + 1}` : ''}` : asset.fileName;
              assetsByCategory[cat].push({ url: asset.url, caption, description: (asset as any).description, assetId: asset.id });
            }
            // Map asset categories to section keys (sealcoat → seal-coat)
            const ASSET_CAT_TO_SECTION: Record<string, string> = {
              asphalt: 'asphalt',
              concrete: 'concrete',
              grading: 'grading',
              sealcoat: 'seal-coat',
              striping: 'striping',
            };

            // Per-section markup + H&D folding is handled by the shared canonical
            // helper (computeSectionFinancials), so preview, signature page, PDF, and
            // the builder all produce identical section totals.
            const markupDataRaw = effectiveEstimate.markupData || {};

            const categories: ProjectCategory[] = [];

            for (const sec of PROJECT_SECTIONS) {
              // Find the writeup group for this section
              const writeupGroup = scopeGroups.find((g: any) => g.id === sec.key);

              // Calculate category total from line items (raw subtotal + markup)
              const sectionItems = allItems.filter((li: any) => {
                const s = (li.section as string) || '';
                if (sec.key === 'asphalt') return s === 'asphalt' || s === 'asphalt-grading' || s === 'asphalt-rnr' || s === 'asphalt-overlay';
                if (sec.key === 'concrete') return s === 'concrete' || s === 'concrete-grading';
                return s === sec.key;
              });
              const sectionTotal = computeSectionFinancials(
                sec.key, allItems, lchBySection,
                markupDataRaw as Record<string, { markupPercent: number }[]>,
                subcontractorsBySection,
              ).total;

              // Skip categories with no writeup content, no line items AND no
              // subcontractors — a subcontractor-only service must still show, or
              // its cost is in the grand total but in no category above it.
              if (!writeupGroup && sectionItems.length === 0 && !subcontractorsBySection[sec.key]) continue;

              // Build proposal groups from writeup sections/rows
              const groups: WriteupGroup[] = [];

              if (writeupGroup) {
                // Flatten top-level rows + all sub-section rows into a single list
                // (sub-section titles like "Remove & Replace" / "Overlay" are not shown on the proposal)
                const allRows: string[] = [];

                for (const r of (writeupGroup.rows || [])) {
                  if (r.enabled) allRows.push(renderRowContent(r.content));
                }

                for (const section of (writeupGroup.sections || [])) {
                  for (const r of (section.rows || [])) {
                    if (r.enabled) allRows.push(renderRowContent(r.content));
                  }
                }

                if (allRows.length > 0) {
                  groups.push({
                    name: '',
                    items: allRows,
                  });
                }
              }

              if (sec.key === 'striping' && sectionItems.length > 0) {
                const lineItemStrings = sectionItems
                  .filter((li: any) => !li.hideFromProposal)
                  .map((li: any) => `${li.quantity} ${li.unit} — ${li.description}`);
                if (lineItemStrings.length > 0) groups.push({ name: '', items: lineItemStrings });
              }

              // Only include category if it has writeup content or line items with totals
              // Find assets for this category section
              const sectionAssetKey = Object.entries(ASSET_CAT_TO_SECTION).find(([, v]) => v === sec.key)?.[0] || '';
              const sectionAssets = assetsByCategory[sectionAssetKey] || [];

              if (groups.length > 0 || sectionTotal > 0 || sectionAssets.length > 0) {
                categories.push({
                  name: sec.label,
                  groups,
                  categoryTotal: fmt(sectionTotal),
                  assets: sectionAssets,
                });
              }
            }

            const markupPct = effectiveEstimate.defaultMarkup ?? 0;
            const markupDisplay = markupPct > 0 ? (markupPct * 100).toFixed(0) + '%' : 'Custom';

            // Grand total DERIVED from the same line items + markup + H&D + subcontractors
            // + discount the categories above are built from — never the stored
            // `estimate.total` scalar, which drifts stale when a bid's scope grows without
            // the Bid Totals panel re-saving. Guarantees the Final Base Bid Total equals the
            // sum of the category totals shown. (Unit pricing renders via its own path.)
            const estFin = effectiveEstimate.isUnitPricing
              ? null
              : computeEstimateFinancials({
                  lineItems: allItems as any,
                  lchRows: parseLchData(effectiveEstimate.lchData),
                  markupData: markupDataRaw as Record<string, { markupPercent: number }[]>,
                  subcontractors: parseSubcontractors((effectiveEstimate as any).subcontractorsData),
                  discountType: effectiveEstimate.discountType,
                  discountValue: effectiveEstimate.discountValue,
                });

            const realProposal: Proposal = {
              ...sampleProposal,
              ...companyFields,
              aboutSections,
              aboutCompany: '',
              terms: termsText,
              client: estimate.clientCompany || estimate.clientName || sampleProposal.client,
              clientContact: estimate.clientName || sampleProposal.clientContact,
              clientEmail: estimate.clientEmail || sampleProposal.clientEmail,
              clientPhone: estimate.clientPhone || sampleProposal.clientPhone,
              // Client mailing address comes from the linked client record, NOT the job site.
              // (Falls back to empty rather than the job address or sample data so a real
              // proposal never shows the project location — or fake demo data — as the client address.)
              clientAddress: [estimate.client?.address, estimate.client?.city, estimate.client?.state, estimate.client?.zip]
                .filter(Boolean).join(', '),
              clientAddressLine1: estimate.client?.address || '',
              clientAddressLine2: [estimate.client?.city, estimate.client?.state, estimate.client?.zip].filter(Boolean).join(', '),
              projectTitle: effectiveEstimate.title || sampleProposal.projectTitle,
              projectAddress: [estimate.jobAddress, estimate.jobCity, estimate.jobState, estimate.jobZip]
                .filter(Boolean).join(', ') || sampleProposal.projectAddress,
              proposalNumber: proposalRecord?.proposalNumber || sampleProposal.proposalNumber,
              revisionCount: revisionCount(proposalRecord?.revisionNumber),
              estimatorName: estimate.createdByUser?.displayName || sampleProposal.estimatorName,
              scope: effectiveEstimate.scopeOfWork || '',
              lineItems,
              categories: categories.length > 0 ? categories : sampleProposal.categories,
              subtotal: fmt(effectiveEstimate.subtotal),
              markup: markupDisplay,
              markupAmount: fmt(effectiveEstimate.markupAmount),
              total: fmt(estFin ? estFin.grandTotalBeforeDiscount : effectiveEstimate.total),
              discountType: effectiveEstimate.discountType || 'percentage',
              discountValue: effectiveEstimate.discountValue || 0,
              showDiscountOnProposal: effectiveEstimate.showDiscountOnProposal || false,
              discountAmount: (() => {
                if (estFin) return estFin.discountAmount > 0 ? fmt(estFin.discountAmount) : undefined;
                const dv = effectiveEstimate.discountValue || 0;
                if (dv <= 0) return undefined;
                const dt = effectiveEstimate.discountType || 'percentage';
                const da = dt === 'percentage' ? effectiveEstimate.total * (dv / 100) : dv;
                return fmt(da);
              })(),
              finalTotal: (() => {
                if (estFin) return fmt(estFin.total);
                const dv = effectiveEstimate.discountValue || 0;
                if (dv <= 0) return fmt(effectiveEstimate.total);
                const dt = effectiveEstimate.discountType || 'percentage';
                const da = dt === 'percentage' ? effectiveEstimate.total * (dv / 100) : dv;
                return fmt(Math.max(0, effectiveEstimate.total - da));
              })(),
              // Preset ExclusionItem ids OR estimator-added custom items carrying their
              // text inline — see lib/inclusion-selection.ts. No selections = nothing shown.
              inclusions: resolveSelectionTexts(effectiveEstimate.selectedInclusionIds, inclItems),
              exclusions: resolveSelectionTexts(effectiveEstimate.selectedExclusionIds, exclItems),
              date: proposalRecord
                ? new Date(proposalRecord.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
              // The estimate's date wins: `effectiveEstimate` is the frozen snapshot
              // once sent and the live estimate while draft. proposalRecord.expiresAt
              // is only the creation-time copy and goes stale as soon as the date is
              // changed, which is what put a different date on the proposal and the
              // PDF than the one picked. See lib/valid-through.ts.
              validUntil: validThroughLabel(effectiveEstimate, proposalRecord),
              mapAssets: assetsByCategory['map'] || [],
              addendumAssets: assetsByCategory['addendum'] || [],
              pinLetters: Object.keys(pinLetterMap).length > 0 ? pinLetterMap : undefined,
              // Signature data (if proposal has been signed)
              signatureDataUrl: proposalRecord?.signatureDataUrl || undefined,
              signatureName: proposalRecord?.signatureName || undefined,
              signedAt: proposalRecord?.signedAt || undefined,
              additionalNotes: effectiveEstimate.additionalNotes || '',
              projectConditions: effectiveEstimate.projectConditions || '',
              coverLetter: settings.includeCoverLetter && settings.coverLetter ? settings.coverLetter : undefined,
              isUnitPricing: !!effectiveEstimate.isUnitPricing,
              unitPriceItems: effectiveEstimate.isUnitPricing
                ? (effectiveEstimate.lineItems || [])
                    .filter((li: any) => li.section === 'unit-price')
                    .map((li: any) => ({
                      item: li.notes || '',
                      description: li.description || '',
                      unitType: li.unit || 'Each',
                      unitPrice: li.rate || 0,
                      quantity: li.quantity || 0,
                      total: li.lineTotal || 0,
                    }))
                : undefined,
              options: (() => {
                const raw = (effectiveEstimate as any).optionsData;
                if (!raw || !Array.isArray(raw) || raw.length === 0) return undefined;
                return raw.map((r: any) => ({ id: r.id || '', name: r.name || '', description: r.description || '', cost: Number(r.cost) || 0 }));
              })(),
            };

            setProposal(realProposal);
            return;
          } catch (err: any) {
            console.error('Failed to load real proposal data:', err);
            // Only fall through to sample data for template/settings previews — not for real proposal views
            if (!isPreviewOnly && !isSettingsPreview && (id || estimateId)) {
              const status = err?.response?.status ?? err?.status;
              setLoadError({
                status,
                message:
                  status === 403
                    ? "You don't have permission to view this proposal."
                    : status === 404
                      ? 'Proposal not found.'
                      : 'Failed to load this proposal.',
              });
              return;
            }
          }
        }

        // Fallback: sample data with company branding (template/settings previews only)
        setProposal({
          ...sampleProposal,
          ...companyFields,
          aboutSections,
          aboutCompany: '',
          terms: termsText,
        });
      } catch {
        // Only show sample data for preview modes — never for real proposal views
        if (!isPreviewOnly && !isSettingsPreview && (id || estimateId)) {
          setLoadError({ message: 'Failed to load this proposal.' });
          return;
        }
        setProposal(sampleProposal);
      }
    };

    loadData();
  }, []);

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printingNotes, setPrintingNotes] = useState(false);

  // Print the estimate's Project Notes as a standalone INTERNAL sheet. This is a
  // browser print of a separate document — it never touches the proposal PDF or
  // the send-to-client flow, so the notes can't reach the customer's copy.
  const handlePrintNotes = async (): Promise<void> => {
    if (!estimateId) {
      alert('No estimate is linked to this proposal, so there are no project notes to print.');
      return;
    }
    setPrintingNotes(true);
    try {
      const notes = await estimatesService.getFieldNotes(estimateId);
      printProjectNotes({ projectName: proposal?.projectTitle, notes });
    } catch {
      alert('Could not load the project notes. Please try again.');
    } finally {
      setPrintingNotes(false);
    }
  };

  const [overviewLoading, setOverviewLoading] = useState(false);

  // Build + print a SCOPE-ONLY Project Overview for the field team. Re-fetches
  // the estimate (scope/materials/labor/equipment/map), field notes, and the
  // saved map image, then prints a standalone sheet. This never touches the
  // proposal PDF or the send-to-client flow, so it can't reach the customer.
  const handleProjectOverview = async (): Promise<void> => {
    if (!estimateId) {
      alert('No estimate is linked to this proposal, so an overview cannot be built.');
      return;
    }
    setOverviewLoading(true);
    try {
      const [estimate, notes, assets] = await Promise.all([
        estimatesService.getById(estimateId),
        estimatesService.getFieldNotes(estimateId).catch(() => []),
        assetsService.list(estimateId).catch(() => []),
      ]);
      printProjectOverview({
        proposal: {
          company: proposal?.company,
          projectTitle: proposal?.projectTitle,
          client: proposal?.client,
          clientContact: proposal?.clientContact,
          clientPhone: proposal?.clientPhone,
          projectAddress: proposal?.projectAddress,
          estimatorName: proposal?.estimatorName,
          proposalNumber: proposal?.proposalNumber,
          categories: proposal?.categories,
          inclusions: proposal?.inclusions,
          exclusions: proposal?.exclusions,
        },
        estimate: estimate as any,
        notes,
        assets,
      });
    } catch {
      alert('Could not build the project overview. Please try again.');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleDownloadPDF = async (): Promise<void> => {
    if (!proposalRecordId && !estimateId) {
      alert('No proposal or estimate found.');
      return;
    }
    setPdfGenerating(true);
    try {
      const blob = proposalRecordId
        ? await proposalsService.downloadPdf(proposalRecordId)
        : await estimatesService.downloadPdf(estimateId!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = proposalRecordId && proposal
        ? `${proposal.proposalNumber || 'Proposal'} - ${proposal.projectTitle || 'Untitled'}.pdf`
        : `Draft - ${proposal?.projectTitle || 'Estimate'}.pdf`;
      a.download = fileName.replace(/[/\\?%*:|"<>]/g, '-');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  if (loadError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '12px', padding: '24px' }}>
        <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>{loadError.message}</p>
        {loadError.status === 403 && (
          <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', maxWidth: '420px' }}>
            Ask a manager or admin to share this proposal, or have them add you as the creator of the underlying estimate.
          </p>
        )}
        <button
          onClick={() => navigate(backTo)}
          style={{ marginTop: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#374151', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
        >
          Go back
        </button>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Loading preview...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 80px)', backgroundColor: 'white', margin: '-24px', borderRadius: '0' }}>
      {/* Top Bar */}
      <div className="pp-topbar" style={isSettingsPreview ? { background: 'transparent', borderBottom: 'none' } : undefined}>
        {/* Back Button — only shown for settings preview and estimate preview modes.
            Normal proposal detail pages use the header bar back link instead. */}
        {(isSettingsPreview || isPreviewOnly) && (
          <button
            onClick={() => {
              if (isSettingsPreview) {
                navigate('/settings/company?tab=templates');
              } else if (isPreviewOnly && backTo.startsWith('/estimates/')) {
                const backTab = proposal?.isUnitPricing ? 'unit-price' : 'bid-totals';
                navigate(`${backTo}?tab=${backTab}`);
              } else {
                navigate(backTo);
              }
            }}
            className="pp-back"
          >
            <ArrowLeft size={20} />
            <span>{isSettingsPreview ? 'Back to Company Settings' : 'Back to Estimate'}</span>
          </button>
        )}

        {/* Center: Template selector (settings preview) or Proposal name (normal) */}
        {isSettingsPreview ? (
          <div className="pp-template-tabs">
            {[
              { id: 'classic', label: 'Classic' },
              { id: 'modern', label: 'Modern' },
              { id: 'bold', label: 'Bold' },
              { id: 'technical', label: 'Technical' },
              { id: 'compact', label: 'Compact' },
              { id: 'monolith', label: 'Monolith' },
            ].map((t) => (
              <button
                key={t.id}
                className={`pp-template-tab${template === t.id ? ' active' : ''}`}
                onClick={() => setTemplate(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : (
          <h1 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', flex: 1, textAlign: 'center' }}>
            {proposal.proposalNumber ? `${proposal.proposalNumber}${(proposal.revisionCount ?? 0) > 0 ? ` · Revision ${proposal.revisionCount}` : ''} — ${proposal.projectTitle}` : proposal.projectTitle}
          </h1>
        )}

        {/* Action Buttons */}
        {isSettingsPreview ? (
          <div className="pp-actions">
            <button
              onClick={async () => {
                setPdfGenerating(true);
                try {
                  const blob = await proposalsService.downloadPreviewPdf(template);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'Proposal-Preview.pdf';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('Preview PDF generation failed:', err);
                  alert('PDF generation failed. Please try again.');
                } finally {
                  setPdfGenerating(false);
                }
              }}
              disabled={pdfGenerating}
            >
              <Download size={16} />
              <span>{pdfGenerating ? 'Generating…' : 'Download PDF'}</span>
            </button>
          </div>
        ) : (
        <div className="pp-actions">
          {invoiceId && (
            <button onClick={() => navigate(`/invoices/${invoiceId}`)}>
              <span>View Invoice</span>
            </button>
          )}
          <button
            className="pp-print-notes-btn"
            onClick={handlePrintNotes}
            disabled={printingNotes}
            title="Print internal project notes — never sent to the customer"
          >
            <Printer size={16} />
            <span>{printingNotes ? 'Loading…' : 'Print Notes'}</span>
          </button>
          {/* Not offered on unit price bids. The Project Overview is a scope sheet
              built from the writeup, and a unit price bid does not work that way —
              its unit price schedule is the scope. Same reason the customer-facing
              proposal drops scope in this mode (see TemplateRenderer). Regular
              estimates and proposals are unaffected. */}
          {!proposal?.isUnitPricing && (
            <button
              className="pp-project-overview-btn"
              onClick={handleProjectOverview}
              disabled={overviewLoading}
              title="Print a Project Overview for the field team — internal, never sent to the customer"
            >
              <ClipboardList size={16} />
              <span>{overviewLoading ? 'Building…' : 'Project Overview'}</span>
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={pdfGenerating}
          >
            <Download size={16} />
            <span>{pdfGenerating ? 'Generating…' : 'Download PDF'}</span>
          </button>
        </div>
        )}
      </div>


      {/* Canvas Area */}
      <div className="pp-canvas">
        <TemplateRenderer template={template} proposal={proposal} />
      </div>
    </div>
  );
}
